import {
  all,
  call,
  delay,
  fork,
  getContext,
  put,
  retry,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  CHANNEL,
  IDocument,
  IRecordedInterviewEntry,
  IRecordedInterviewProcessingStatus,
  IWebsocketServerMessage,
  RecordedInterviewSystemStatus,
  TOPIC,
  DocStatus,
  ICalendlyEvent,
} from 'types';

import { toast } from 'react-toastify';
import { websocketActions, WebsocketState } from 'websocket-utility';
import * as api from '../../api/api';
import { setNavigateStudentRecordAction } from '../../middleware/actions';
import { IAnyObj } from '../../types';
import { getLeafNodeObj } from '../../utilities/getLeafNodeValue';
import { getAllTasksFromRequirements } from '../../utilities/transformRequirements';
import { logout } from '../auth/actions';
import { handleOpenedStudentRecords } from '../dashboard/actions';
import { AuthState } from '../auth/types';
import { getAuth, getStudent, getUI, getWebsocket } from '../selectors';
import { UIState } from '../ui/types';
import { ApplicationState } from '../index';
import * as actions from './actions';
import { Pages as ConsentPages } from './consents/types';
import * as coreActions from './core/actions';
import { CoreActionTypes } from './core/types';
import * as docActions from './documents/actions';
import { DocActionTypes } from './documents/types';
import { EnrolmentStatusActionTypes } from './enrolmentStatus/types';
import { getApplicantOffer, updateApplicantRequirementStatusSuccess } from '../apply/actions';
import * as finDocActions from './financeDocuments/actions';
import { DocActionTypes as FinDocActionTypes } from './financeDocuments/types';
import * as followUpDocActions from './followUpDocuments/actions';
import { DocActionTypes as FollowUpDocActionTypes } from './followUpDocuments/types';
import { getCountryOfVisaApplication } from './helpers';
import { Pages as HistoryPages } from './history/types';
import * as offerActions from './offer/actions';
import { RecordedInterviewActionTypes } from './offer/types';
import { Pages as PersonalInfoPages } from './personalInfo/types';
import { Pages as InterviewPages } from './preCasInterview/types';
import { StudentActionTypes, StudentState } from './types';
import { Pages as VisaPages } from './visaPrepForm/types';
import { getDocDetails } from '../../utilities/documents';
import { API_CALLS } from '../../utilities/constants';
import { getCalendlyInterviewsInfoStatus } from '@/utilities/getCalendlyInterviewsInfoStatus';

function* handleSaveMessage(action: any): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveMessage');
      return;
    }
    const res: any = yield call(
      api.saveMessage,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload,
    );
    if (res) {
      yield put(actions.saveMessageSuccess(action.payload));
    }
  } catch {
    yield put(actions.saveMessageFailure(action.payload));
  }
}

function* handleMarkMessagesRead(action: ReturnType<typeof actions.markMessagesRead>): Generator {
  try {
    const auth = yield select(getAuth);
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleMarkMessagesRead');
      return;
    }
    const messageIds = (action.payload || []).filter(message => !!message);

    yield call(
      api.markMessagesRead,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      messageIds,
    );
    yield put(actions.markMessagesReadSuccess(messageIds, auth.role));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.markMessagesReadFailure(err.message));
    } else {
      yield put(actions.markMessagesReadFailure('An unknown error occured.'));
    }
  }
}

function* handleMarkMessageUnread(action: ReturnType<typeof actions.markMessageUnread>): Generator {
  try {
    const auth = yield select(getAuth);
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleMarkMessageUnread');
      return;
    }
    const messageId = action.payload;

    yield call(
      api.markMessageUnread,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      messageId,
    );
    yield put(actions.markMessageUnreadSuccess(messageId, auth.role));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.markMessageUnreadFailure(err.message));
    } else {
      yield put(actions.markMessageUnreadFailure('An unknown error occured.'));
    }
  }
}

function* handleSavePersonalInfo(action: ReturnType<typeof actions.savePI>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSavePersonalInfo');
      return;
    }

    let cas: any;
    switch (action.payload.source) {
      case PersonalInfoPages.PAGE1:
        cas = yield call(
          api.updateStudent,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            givenName: student.personalInfo.givenName,
            surname: student.personalInfo.surname,
            middleName: student.personalInfo.middleName,
            hasOtherNames: student.personalInfo.hasOtherNames,
            otherNames: student.personalInfo.otherNames,
          },
          'pi-step1',
        );
        break;
      case PersonalInfoPages.PAGE2:
        const nationalities = student.personalInfo.otherNationalities!.map(row => {
          return {
            nationality: row.nationality,
            isCurrent: row.isCurrent ?? false,
          };
        });

        const countryOfVisaApplication = getCountryOfVisaApplication(student.personalInfo);

        cas = yield call(
          api.updateStudent as any,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            gender: student.personalInfo.gender,
            dob: student.personalInfo.dob,
            birthCountry: student.personalInfo.birthCountry,
            nationality: student.personalInfo.nationality,
            appliedForEUSS: student.personalInfo.appliedForEUSS,
            grantedEUSSStatus: student.personalInfo.grantedEUSSStatus,
            applyingFromHomeCountry: student.personalInfo.applyingFromHomeCountry,
            countryOfVisaApplication,
            livedInUKLast12Months: student.personalInfo.livedInUKLast12Months,
            otherCountryReason: student.personalInfo.otherCountryReason,
            hasOtherNationality: student.personalInfo.hasOtherNationality,
            otherNationalities: nationalities,
          },
          'pi-step2',
        );
        break;
      case PersonalInfoPages.PAGE3:
        const PIP3cb: any = api.updateStudent;
        cas = yield call(
          PIP3cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            phoneNumber: student.personalInfo.phoneNumber,
            homeAddress: student.personalInfo.homeAddress,
            city: student.personalInfo.city,
            postCode: student.personalInfo.postCode,
            country: student.personalInfo.country,
          },
          'pi-step3',
        );
        break;
      case PersonalInfoPages.PAGE4:
        const PIP4cb: any = api.updateStudent;
        cas = yield call(
          PIP4cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            hasCriminalConvictions: student.personalInfo.hasCriminalConvictions,
            criminalConvictionReason: student.personalInfo.criminalConvictionReason,
          },
          'pi-step4',
        );
        break;
      case PersonalInfoPages.PAGE5:
        const PIP5cb: any = api.updateStudent;
        cas = yield call(
          PIP5cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            studentNo: student.personalInfo.studentNo,
            courses: student.offer.courses!,
            casReceived: student.offer.casReceived,
          },
          'pi-step5',
        );
        break;
      case PersonalInfoPages.PAGE5_1: {
        const PIP5_1cb: any = api.updateStudent;
        cas = yield call(
          PIP5_1cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          { courses: student.offer.courses! },
          'pi-step5_1',
        );
        break;
      }
      case PersonalInfoPages.PAGE6:
        const PIP6cb: any = api.updateStudent;
        cas = yield call(
          PIP6cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            hasEnglishTest: student.personalInfo.hasEnglishTest,
            englishTestType: student.personalInfo.englishTestType,
            englishTestTypeOther: student.personalInfo.englishTestTypeOther,
            englishTestNotReqdReason: student.personalInfo.englishTestNotReqdReason,
            englishTestNotReqdReasonOther: student.personalInfo.englishTestNotReqdReasonOther,
            qualification: student.personalInfo.qualification,
            qualificationOther: student.personalInfo.qualificationOther,
          },
          'pi-step6',
        );
        break;
      case PersonalInfoPages.PAGE7:
        const PIP7cb: any = api.updateStudent;
        cas = yield call(
          PIP7cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            passportStatus: student.personalInfo.passportStatus,
            passportNumber: student.personalInfo.passportNumber,
            passportExpectedDate: student.personalInfo.passportExpectedDate,
            passportExpiryDate: student.personalInfo.passportExpiryDate,
          },
          'pi-step7',
        );
        break;
      case PersonalInfoPages.PAGE8:
        const PIP8cb: any = api.updateStudent;
        cas = yield call(
          PIP8cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            withAgent: student.personalInfo.withAgent || '',
          },
          'pi-step8',
        );
        break;

      case PersonalInfoPages.PAGE9:
        const PIP9cb: any = api.updateStudent;
        cas = yield call(
          PIP9cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            visaSupport: student.offer.visaSupport,
          },
          'pi-step9',
        );
        break;
      case PersonalInfoPages.PAGE10:
        const PIP10cb: any = api.updateStudent;
        cas = yield call(
          PIP10cb,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            useWhatsapp: student.personalInfo.useWhatsapp,
            whatsAppPhoneNumber: student.personalInfo.whatsAppPhoneNumber,
          },
          'pi-step10',
        );
        break;
    }
    yield put(actions.savePISuccess(cas));
    yield call(handleNavigationAfterSave);
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.savePIFailure(err.message));
    } else {
      yield put(actions.savePIFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveHistory(action: ReturnType<typeof actions.saveHistory>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveHistory');
      return;
    }
    let cas;
    switch (action.payload.source) {
      case HistoryPages.PAGE1:
        cas = yield call(
          api.updateStudent,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            lastCourse: student.history.lastCourse,
            lastCourseOther: student.history.lastCourseOther,
            lastCourseYear: student.history.lastCourseYear,
            hasOverseasTravel: student.history.hasOverseasTravel,
            overseasTravelTimes: student.history.overseasTravelTimes,
            hasUkTravel: student.history.hasUkTravel,
            ukTravelTimes: student.history.ukTravelTimes,
            hasPreviousUkStudy: student.history.hasPreviousUkStudy,
            hasPreviousUkStudyVisa: student.history.hasPreviousUkStudyVisa,
            previousUkStudyVisas: student.history.previousUkStudyVisas,
            lastCourseLevel: student.history.lastCourseLevel,
            lowerOrSameLevelReason: student.history.lowerOrSameLevelReason,
          },
          'h-step1',
        );
        break;
      case HistoryPages.PAGE2:
        cas = yield call(
          api.updateStudent,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            hasOtherUkVisas: student.history.hasOtherUkVisas,
            otherUkVisas: student.history.otherUkVisas,
            hasLeaveToRemain: student.history.hasLeaveToRemain,
            hasBorderRefusal: student.history.hasBorderRefusal,
            borderRefusalReason: student.history.borderRefusalReason,
            hasUkVisaRefusals: student.history.hasUkVisaRefusals,
            ukVisaRefusals: student.history.ukVisaRefusals,
            hasOtherVisaRefusals: student.history.hasOtherVisaRefusals,
            otherVisaRefusals: student.history.otherVisaRefusals,
          },
          'h-step2',
        );
        break;
      case HistoryPages.PAGE3:
        cas = yield call(
          api.updateStudent,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            hasDependents: student.offer.hasDependents,
            hasPartner: student.offer.hasPartner,
            partnerVisaStatus: student.offer.partnerVisaStatus,
            hasChildren: student.offer.hasChildren,
            childrenNum: student.offer.childrenNum,
          },
          'h-step3',
        );
        break;
    }
    yield put(actions.saveHistorySuccess(cas));
    yield call(handleNavigationAfterSave);
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveHistoryFailure(err.message));
    } else {
      yield put(actions.saveHistoryFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveConsents(action: ReturnType<typeof actions.saveConsents>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveConsents');
      return;
    }

    let cas;
    switch (action.payload.source) {
      case ConsentPages.PAGE1:
        cas = yield call(
          api.updateStudent,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {},
          'c-step1',
        );
        break;
      case ConsentPages.PAGE2:
        cas = yield call(
          api.updateStudent,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {},
          'c-step2',
        );
        break;
      case ConsentPages.PAGE3:
        cas = yield call(
          api.updateStudent,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {},
          'c-step3',
        );
        break;
      case ConsentPages.PAGE4:
        cas = yield call(
          api.updateStudent,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {},
          'c-step4',
        );
        break;
    }

    yield put(actions.saveConsentsSuccess(cas));
    yield call(handleNavigationAfterSave);
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveConsentsFailure(err.message));
    } else {
      yield put(actions.saveConsentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveDeposit(_action: ReturnType<typeof actions.saveDeposit>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveDeposit');
      return;
    }

    const res: any = yield call(
      api.updateStudent,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      {
        courseFeeDepositReqd: student.financials.courseFeeDepositReqd ?? '',
        courseFeeDeposit: student.financials.courseFeeDeposit ?? 0,
        courseFeeDepositDate: student.financials.courseFeeDepositDate ?? '',
        courseFeeDepositNotReqdReason: student.financials.courseFeeDepositNotReqdReason ?? '',
        accomDepositReqd:
          (student.financials.accomDepositReqd ?? '') !== ''
            ? student.financials.accomDepositReqd
            : 'No - Not Required',
        accomDeposit: student.financials.accomDeposit ?? 0,
        accomDepositDate: student.financials.accomDepositDate ?? '',
      },
      'deposit',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateOrganisationSponsor(action: ReturnType<typeof actions.createOrganisationSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCreateOrganisationSponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.createSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      {
        orgType: data.orgType,
        sponsorName: data.sponsorName,
        isMultiCountry: data.isMultiCountry,
        sponsorshipCovers: data.sponsorshipCovers,
        courseFundType: data.courseFundType,
        courseFundPercentage: data.courseFundPercentage,
        courseFundAmount: data.courseFundAmount,
        livingFundType: data.livingFundType,
        livingFundPercentage: data.livingFundPercentage,
        livingFundAmountType: data.livingFundAmountType,
        livingFundAmount: data.livingFundAmount,
        courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
        courseAndLivingFundType: data.courseAndLivingFundType,
        courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
        courseAndLivingFundAmount: data.courseAndLivingFundAmount,
      },
      'company',
    );

    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateOrganisationSponsor(action: ReturnType<typeof actions.updateOrganisationSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateOrganisationSponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.updateSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      {
        orgType: data.orgType,
        sponsorName: data.sponsorName,
        isMultiCountry: data.isMultiCountry,
        sponsorshipCovers: data.sponsorshipCovers,
        courseFundType: data.courseFundType,
        courseFundPercentage: data.courseFundPercentage,
        courseFundAmount: data.courseFundAmount,
        livingFundType: data.livingFundType,
        livingFundPercentage: data.livingFundPercentage,
        livingFundAmountType: data.livingFundAmountType,
        livingFundAmount: data.livingFundAmount,
        courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
        courseAndLivingFundType: data.courseAndLivingFundType,
        courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
        courseAndLivingFundAmount: data.courseAndLivingFundAmount,
      },
      'company',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveOrganisationSponsor(action: ReturnType<typeof actions.removeOrganisationSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveOrganisationSponsor');
      return;
    }

    const res: any = yield call(
      api.removeSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      'company',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveOrganisationRegion(_action: ReturnType<typeof actions.removeOrganisationRegion>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveOrganisationRegion');
      return;
    }

    const res: any = yield call(
      api.removeRegion,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      'company',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateScholarshipSponsor(action: ReturnType<typeof actions.createScholarshipSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCreateScholarshipSponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.createSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      {
        scholarshipName: data.scholarshipName,
        sponsorshipCovers: data.sponsorshipCovers,
        courseFundType: data.courseFundType,
        courseFundPercentage: data.courseFundPercentage,
        courseFundAmount: data.courseFundAmount,
        livingFundType: data.livingFundType,
        livingFundPercentage: data.livingFundPercentage,
        livingFundAmountType: data.livingFundAmountType,
        livingFundAmount: data.livingFundAmount,
        courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
        courseAndLivingFundType: data.courseAndLivingFundType,
        courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
        courseAndLivingFundAmount: data.courseAndLivingFundAmount,
      },
      'scholarship',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleBulkUpdateScholarshipSponsor(
  action: ReturnType<typeof actions.bulkUpdateScholarshipSponsor>,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      console.log('Refresh Token has Expired - handleBulkUpdateScholarshipSponsor');
      return;
    }

    const payloadData = action.payload.data;
    for (const [index, sponsor] of payloadData.entries()) {
      const { key, data } = sponsor;
      const res: any = yield call(
        api.updateSponsor,
        idToken,
        student.core.university,
        student.core.student_id.split('#')[1],
        student.offer.intakeId,
        student.offer.offerId,
        key,
        {
          scholarshipName: data.scholarshipName,
          sponsorshipCovers: data.sponsorshipCovers,
          courseFundType: data.courseFundType,
          courseFundPercentage: data.courseFundPercentage,
          courseFundAmount: data.courseFundAmount,
          livingFundType: data.livingFundType,
          livingFundPercentage: data.livingFundPercentage,
          livingFundAmountType: data.livingFundAmountType,
          livingFundAmount: data.livingFundAmount,
          courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
          courseAndLivingFundType: data.courseAndLivingFundType,
          courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
          courseAndLivingFundAmount: data.courseAndLivingFundAmount,
        },
        'scholarship',
      );
      if (index === payloadData.length - 1) yield put(actions.saveFinanceInfoSuccess(res));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateScholarshipSponsor(action: ReturnType<typeof actions.updateScholarshipSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateScholarshipSponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.updateSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      {
        scholarshipName: data.scholarshipName,
        sponsorshipCovers: data.sponsorshipCovers,
        courseFundType: data.courseFundType,
        courseFundPercentage: data.courseFundPercentage,
        courseFundAmount: data.courseFundAmount,
        livingFundType: data.livingFundType,
        livingFundPercentage: data.livingFundPercentage,
        livingFundAmountType: data.livingFundAmountType,
        livingFundAmount: data.livingFundAmount,
        courseAndLivingFundSeparate: data.courseAndLivingFundSeparate,
        courseAndLivingFundType: data.courseAndLivingFundType,
        courseAndLivingFundPercentage: data.courseAndLivingFundPercentage,
        courseAndLivingFundAmount: data.courseAndLivingFundAmount,
      },
      'scholarship',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveScholarshipSponsor(action: ReturnType<typeof actions.removeScholarshipSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveScholarshipSponsor');
      return;
    }

    const res: any = yield call(
      api.removeSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      'scholarship',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveScholarshipRegion(_action: ReturnType<typeof actions.removeScholarshipRegion>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveScholarshipRegion');
      return;
    }

    const res: any = yield call(
      api.removeRegion,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      'scholarship',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateEducationSponsor(action: ReturnType<typeof actions.createEducationSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCreateEducationSponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.createSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      {
        loanType: data.loanType,
        country: data.country,
        bankName: data.bankName,
        loanName: data.loanName,
        sponsorshipAmount: data.sponsorshipAmount,
        personName: data.personName,
        loanDesc: data.loanDesc,
        personFirstName: data.personFirstName,
        personSurname: data.personSurname,
        personDesc: data.personDesc,
      },
      'education',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateEducationSponsor(action: ReturnType<typeof actions.updateEducationSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateEducationSponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.updateSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      {
        loanType: data.loanType,
        country: data.country,
        bankName: data.bankName,
        loanName: data.loanName,
        sponsorshipAmount: data.sponsorshipAmount,
        personName: data.personName,
        loanDesc: data.loanDesc,
        personFirstName: data.personFirstName,
        personSurname: data.personSurname,
        personDesc: data.personDesc,
      },
      'education',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveEducationSponsor(action: ReturnType<typeof actions.removeEducationSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveEducationSponsor');
      return;
    }

    const res: any = yield call(
      api.removeSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      'education',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveEducationRegion(_action: ReturnType<typeof actions.removeEducationRegion>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveEducationRegion');
      return;
    }

    const res: any = yield call(
      api.removeRegion,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      'education',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateFamilySponsor(action: ReturnType<typeof actions.createFamilySponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCreateFamilySponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.createSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      {
        familySponsorType: data.sponsorType,
        accountType: data.accountType,
        jointType: data.jointType,
        accountFirstName: data.accountFirstName,
        accountSurname: data.accountSurname,
        otherReason: data.otherReason,
        country: data.country,
        bankName: data.bankName,
        totalAmount: data.totalAmount,
        sponsorshipAmount: data.sponsorshipAmount,
        canWithdrawImmediately: data.canWithdrawImmediately!,
      },
      'family',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateFamilySponsor(action: ReturnType<typeof actions.updateFamilySponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateFamilySponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.updateSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      {
        familySponsorType: data.sponsorType,
        accountType: data.accountType,
        jointType: data.jointType,
        accountFirstName: data.accountFirstName,
        accountSurname: data.accountSurname,
        otherReason: data.otherReason,
        country: data.country,
        bankName: data.bankName,
        totalAmount: data.totalAmount,
        sponsorshipAmount: data.sponsorshipAmount,
        canWithdrawImmediately: data.canWithdrawImmediately!,
      },
      'family',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveFamilySponsor(action: ReturnType<typeof actions.removeFamilySponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveFamilySponsor');
      return;
    }

    const res: any = yield call(
      api.removeSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      'family',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveFamilyRegion(_action: ReturnType<typeof actions.removeFamilyRegion>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveFamilyRegion');
      return;
    }

    const res: any = yield call(
      api.removeRegion,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      'family',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCreateOtherSponsor(action: ReturnType<typeof actions.createOtherSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCreateOtherSponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.createSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      {
        otherDesc: data.otherDesc,
        otherName: data.otherName,
        sponsorshipAmount: data.sponsorshipAmount,
      },
      'other',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateOtherSponsor(action: ReturnType<typeof actions.updateOtherSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateOtherSponsor');
      return;
    }

    const { data } = action.payload;

    const res: any = yield call(
      api.updateSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      {
        otherDesc: data.otherDesc,
        otherName: data.otherName,
        sponsorshipAmount: data.sponsorshipAmount,
      },
      'other',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveOtherSponsor(action: ReturnType<typeof actions.removeOtherSponsor>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveOtherSponsor');
      return;
    }

    const res: any = yield call(
      api.removeSponsor,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.key,
      'other',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleRemoveOtherRegion(_action: ReturnType<typeof actions.removeOtherRegion>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveOtherRegion');
      return;
    }

    const res: any = yield call(
      api.removeRegion,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      'other',
    );
    yield put(actions.saveFinanceInfoSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceInfoFailure(err.message));
    } else {
      yield put(actions.saveFinanceInfoFailure('An unknown error occured.'));
    }
  }
}

function* handleCustomDocumentRequest(action: ReturnType<typeof docActions.requestCustomDoc>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCustomDocumentRequest');
      return;
    }
    const { documentName, documentDescription, userAccess } = action.payload;

    const body = {
      documentName,
      documentDescription,
      userAccess,
    };
    const response = yield call(
      api.requestCustomDocument,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      body,
      false,
    );
    yield put(
      actions.saveDocumentsSuccess({
        docs: response.documents,
        complete: response.docsComplete,
        required: response.docsRequired,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveDocumentsFailure(err.message));
    } else {
      yield put(actions.saveDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleFinanceCustomDocumentRequest(action: ReturnType<typeof finDocActions.requestCustomDoc>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleFinanceCustomDocumentRequest');
      return;
    }
    const { documentName, documentDescription, userAccess } = action.payload;

    const body = {
      documentName,
      documentDescription,
      userAccess,
    };
    const response = yield call(
      api.requestCustomDocument,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      body,
      true,
    );
    yield put(
      actions.saveFinanceDocumentsSuccess({
        finDocs: response.financeDocuments,
        complete: response.docsComplete,
        required: response.docsRequired,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFinanceDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleFollowUpCustomDocumentRequest(
  action: ReturnType<typeof followUpDocActions.requestCustomFollowUpDoc>,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleFollowUpCustomDocumentRequest');
      return;
    }
    const { documentName, documentDescription, userAccess, followUpArea } = action.payload;

    const body = {
      documentName,
      documentDescription,
      userAccess,
    };
    const response = yield call(
      api.requestCustomDocument,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      body,
      false,
      true,
      followUpArea,
    );
    yield put(
      actions.saveFollowUpDocumentsSuccess({
        followUpDocs: response.followUpDocuments,
        beforeComplete: response.beforeTravelDocsComplete,
        beforeRequired: response.beforeTravelDocsReqd,
        afterComplete: response.afterArrivalDocsComplete,
        afterRequired: response.afterArrivalDocsReqd,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFinanceDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleDeleteFile(action: ReturnType<typeof actions.deleteDocumentFile>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleDeleteFile');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;
    const doc: Partial<IDocument> = getDocDetails({
      docs: student.documents,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
    });

    // If there is no files left (all were deleted), set status to Awaiting
    if (doc.fileNames?.length === 0) {
      doc.status = DocStatus.AWAITING;
    }

    const body = {
      ...doc,
      deletedFileName: action.payload.fileName,
      uploadedBy: action.payload.deletedBy,
    };

    const docs = yield call(
      api.DeleteDocsDocument,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body,
    );
    return yield put(
      actions.deleteDocumentFileSuccess(docs.documents, action.payload.fileName, docs.docsComplete, docs.docsRequired),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.deleteDocumentFileFailure(err.message));
    } else {
      yield put(actions.deleteDocumentFileFailure('An unknown error occurred.'));
    }
  }
}

function* handleSaveDocuments(action: ReturnType<typeof actions.saveDocuments>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveDocuments');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;
    const doc = getDocDetails({ docs: student.documents, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;
    const meta = {} as IAnyObj;
    if (
      ['birthCertificate', 'visaRefusals', 'nonUkVisaRefusals', 'otherVisas', 'customRequest', 'studentVisas'].includes(
        sponsorType,
      )
    ) {
      let docRec: any;
      if (sponsorType === 'birthCertificate') {
        docRec = student.documents[sponsorType][docType!][docKey!];
      } else {
        docRec = student.documents[sponsorType][sponsorKey!];
      }
      Object.entries(docRec).forEach(([key, value]) => {
        if (typeof value !== 'object') {
          meta[key] = value;
        }
      });
    }

    // only mark as fixed once a doc has been uploaded
    if (doc.isDocumentFixedPending) {
      delete doc.isDocumentFixedPending;
      if (doc.docCount !== doc.fileNames.length) {
        doc.isDocumentFixed = true;
      } else {
        // return here to prevent an API request as no doc has been uploaded
        yield put(actions.saveFinanceDocumentsFailure('No new document added'));
        return null;
      }
    }

    const body = {
      ...meta,
      ...doc,
      uploadedBy: action.payload.uploadedBy,
    };

    const response = yield call(
      api.saveDocs,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body,
    );
    return yield put(
      actions.saveDocumentsSuccess({
        docs: response.documents,
        complete: response.docsComplete,
        required: response.docsRequired,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveDocumentsFailure(err.message));
    } else {
      yield put(actions.saveDocumentsFailure('An unknown error occurred.'));
    }
  }
}

function* handleDeleteFinanceFile(action: ReturnType<typeof actions.deleteFinanceFile>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleDeleteFinanceFile');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = getDocDetails({ docs: student.financeDocuments, sponsorType, sponsorKey, docType, docKey });

    const isNoFiles = doc.fileNames?.length === 0;

    const body = {
      ...doc,
      deletedFileName: action.payload.fileName,
      uploadedBy: action.payload.deletedBy,
      status: isNoFiles ? 'AWAITING' : doc.status,
    };
    const docs = yield call(
      api.deleteFinanceDocument,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body,
    );
    return yield put(
      actions.deleteFinanceFileSuccess(
        docs.financeDocuments,
        action.payload.fileName,
        docs.docsComplete,
        docs.docsRequired,
      ),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.deleteFinanceFileFailure(err.message));
    } else {
      yield put(actions.deleteFinanceFileFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveFinanceDocuments(action: ReturnType<typeof actions.saveFinanceDocuments>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveFinanceDocuments');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = getDocDetails({ docs: student.financeDocuments, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

    let meta = {};
    if (
      [
        'governmentSponsorship',
        'companySponsorship',
        'educationSponsorship',
        'scholarshipSponsorship',
        'customRequest',
        'familySponsorship',
      ].includes(sponsorType)
    ) {
      meta = student.financeDocuments[sponsorType][sponsorKey!];
    }
    const body = {
      ...meta,
      ...doc,
      uploadedBy: action.payload.uploadedBy,
    };
    const response = yield call(
      api.saveFinanceDocs,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body,
    );
    return yield put(
      actions.saveFinanceDocumentsSuccess({
        finDocs: response.financeDocuments,
        complete: response.docsComplete,
        required: response.docsRequired,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFinanceDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFinanceDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleDeleteFollowUpFile(action: ReturnType<typeof actions.deleteFollowUpFile>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleDeleteFollowUpFile');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = getDocDetails({ docs: student.followUpDocuments, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

    const body = {
      ...doc,
      deletedFileName: action.payload.fileName,
      uploadedBy: action.payload.deletedBy,
    };

    const docs: any = yield call(
      api.deleteFollowUpDocument,
      idToken,
      student.core.university,
      student.core.student_number,
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body,
    );

    return yield put(actions.deleteFollowUpFileSucess(docs, action.payload.fileName));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.deleteFollowUpFileFailure(err.message));
    } else {
      yield put(actions.deleteFollowUpFileFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveFollowUpDocuments(action: ReturnType<typeof actions.saveFollowUpDocuments>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveFollowUpDocuments');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = getDocDetails({ docs: student.followUpDocuments, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

    const meta = {} as IAnyObj;
    if (['beforeTravel', 'afterArrival'].includes(sponsorType)) {
      Object.entries(doc).forEach(([key, value]) => {
        if (typeof value !== 'object') {
          meta[key] = value;
        }
      });
    }

    const body = {
      ...meta,
      ...doc,
      uploadedBy: action.payload.uploadedBy,
    };

    const response = yield call(
      api.saveFollowUpDocs,
      idToken,
      student.core.university,
      student.core.student_number,
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body,
    );

    yield put(
      actions.saveFollowUpDocumentsSuccess({
        followUpDocs: response.followUpDocuments,
        beforeComplete: response.beforeTravelDocsComplete,
        beforeRequired: response.beforeTravelDocsReqd,
        afterComplete: response.afterArrivalDocsComplete,
        afterRequired: response.afterArrivalDocsReqd,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFollowUpDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFollowUpDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveFollowUpDocumentsShareCode(
  action: ReturnType<typeof actions.saveFollowUpDocumentsShareCode>,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveFollowUpDocumentsShareCode');
      return;
    }
    const { data, docKey } = action.payload;

    const doc = {
      ...(student as any).followUpDocuments.beforeTravel?.immigrationStatusShareCode?.[docKey],
    };

    const body = {
      ...doc,
      ...data,
      status: 'UPLOADED_OK',
    };
    const response = yield call(
      api.saveFollowUpDocs,
      idToken,
      student.core.university,
      student.core.student_number,
      student.offer.intakeId,
      student.offer.offerId,
      'beforeTravel',
      null,
      'immigrationStatusShareCode',
      docKey,
      body,
    );
    yield put(
      actions.saveFollowUpDocumentsSuccess({
        followUpDocs: response.followUpDocuments,
        beforeComplete: response.beforeTravelDocsComplete,
        beforeRequired: response.beforeTravelDocsReqd,
        afterComplete: response.afterArrivalDocsComplete,
        afterRequired: response.afterArrivalDocsReqd,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveFollowUpDocumentsFailure(err.message));
    } else {
      yield put(actions.saveFollowUpDocumentsFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveRisks(action: ReturnType<typeof actions.updateRisks>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveRisks');
      return;
    }
    const { source, clearedRisks } = action.payload;

    const risks = yield call(
      api.saveRisks,
      idToken,
      student.core.university,
      student.core.student_number,
      student.offer.intakeId,
      student.offer.offerId,
      source,
      clearedRisks,
    );
    yield put(actions.updateRisksSuccess(risks));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.updateRisksFailure(err.message));
    } else {
      yield put(actions.updateRisksFailure('An unknown error occurred.'));
    }
  }
}

function* handleToggleDocument(
  action: ReturnType<
    | typeof docActions.approveDoc
    | typeof docActions.rejectDoc
    | typeof docActions.deleteDoc
    | typeof docActions.acknowledgeDoc
  >,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleToggleDocument');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey, reason = '', status = null } = action.payload;

    const response = yield call(
      api.toggleDocStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      reason,
      status,
    );
    yield put(
      docActions.toggleDocStatusSuccess({
        docs: response.documents,
        complete: response.docsComplete,
        required: response.docsRequired,
      }),
    );
  } catch (err) {
    yield put(docActions.toggleDocStatusFailure(err.message));
  }
}

function* handleToggleFollowUpDocument(
  action: ReturnType<
    typeof followUpDocActions.approveDoc | typeof followUpDocActions.rejectDoc | typeof followUpDocActions.deleteDoc
  >,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleToggleFollowUpDocument');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey, reason = '', status = null } = action.payload;

    const response = yield call(
      api.toggleFollowUpDocStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      reason,
      status,
    );
    yield put(
      followUpDocActions.toggleDocStatusSuccess({
        followUpDocs: response.followUpDocuments,
        beforeComplete: response.beforeTravelDocsComplete,
        beforeRequired: response.beforeTravelDocsReqd,
        afterComplete: response.afterArrivalDocsComplete,
        afterRequired: response.afterArrivalDocsReqd,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(followUpDocActions.toggleDocStatusFailure(err.message));
    } else {
      yield put(followUpDocActions.toggleDocStatusFailure('An unknown error occured.'));
    }
  }
}

function* handleToggleFinanceDocument(
  action: ReturnType<
    | typeof finDocActions.approveDoc
    | typeof finDocActions.rejectDoc
    | typeof finDocActions.deleteDoc
    | typeof finDocActions.acknowledgeDoc
  >,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleToggleFinanceDocument');
      return;
    }
    const { sponsorType, sponsorKey, docType, docKey, reason = '', status = null } = action.payload;

    // special case for when the uni might request another document while rejecting the current one
    // e.g. a bank letter instead of a bank statement
    let isCustomDocRequested = false;
    if (status === 'REJECTED') {
      type IRejectDoc = ReturnType<typeof finDocActions.rejectDoc>;
      const rejectAction = action as IRejectDoc;
      isCustomDocRequested = rejectAction.payload.isCustomDocRequested ?? false;
    }

    const response = yield call(
      api.toggleFinDocStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      reason,
      status,
      isCustomDocRequested,
    );
    yield put(
      finDocActions.toggleDocStatusSuccess({
        finDocs: response.financeDocuments,
        complete: response.docsComplete,
        required: response.docsRequired,
      }),
    );
  } catch (err) {
    yield put(finDocActions.toggleDocStatusFailure(err));
  }
}

function* handleSaveInterviewDocuments(action: ReturnType<typeof actions.saveInterviewDocuments>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveInterviewDocuments');
      return;
    }
    const { interviewType, eventType, eventKey } = action.payload;

    const calendlyEvent = student.calendlyEvents.find(event => event.eventType === eventType);

    if (calendlyEvent) {
      const fileNames = calendlyEvent.scheduledEvents[eventKey]?.fileNames ?? [];
      const eventName = calendlyEvent.name ?? '';

      const { calendlyEvent: updatesCalendlyEvent } = yield call(
        api.saveInterviewDocs,
        idToken,
        student.core.university,
        student.core.student_id.split('#')[1],
        eventName,
        interviewType,
        eventType,
        eventKey,
        fileNames,
      );
      return yield put(actions.saveInterviewDocumentsSuccess(updatesCalendlyEvent));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveInterviewDocumentsFailure(err.message));
    } else {
      yield put(actions.saveInterviewDocumentsFailure('An unknown error occurred.'));
    }
  }
}

function* handleDeleteInterviewFile(action: ReturnType<typeof actions.deleteInterviewFile>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleDeleteInterviewFile');
      return;
    }
    const { interviewType, eventType, eventKey } = action.payload;

    const body = {
      deletedFileName: action.payload.fileName,
      uploadedBy: action.payload.deletedBy,
    };

    const { calendlyEvent } = yield call(
      api.deleteInterviewDocument,
      idToken,
      student.core.university,
      student.core.student_number,
      interviewType,
      eventType,
      eventKey,
      body,
    );

    return yield put(actions.deleteInterviewFileSuccess(calendlyEvent, action.payload.fileName));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.deleteInterviewFileFailure(err.message));
    } else {
      yield put(actions.deleteInterviewFileFailure('An unknown error occurred.'));
    }
  }
}

function* handleRequestRecordedInterview(action: ReturnType<typeof actions.onRequestRecordedInterview>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRequestRecordedInterview');
      return;
    }

    const { interviewId, note } = action.payload;

    const { interviews } = yield call(
      api.requestStudentRecordedInterview,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      interviewId,
      note,
    );
    yield put(actions.onRequestRecordedInterviewSuccess(interviews));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.onRequestRecordedInterviewFailure(err.message));
    } else {
      yield put(actions.onRequestRecordedInterviewFailure('An unknown error occurred.'));
    }
  }
}

function* handleRequestScheduledInterview(action: ReturnType<typeof actions.requestScheduledInterview>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRequestScheduledInterview');
      return;
    }

    const { eventType } = action.payload;

    const { calendlyEvent } = yield call(
      api.toggleScheduledInterviewStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      { eventType, note: '' },
      'request',
    );
    yield put(actions.toggleScheduledInterviewStatusSuccess(calendlyEvent));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.toggleScheduledInterviewStatusFailure(err.message));
    } else {
      yield put(actions.toggleScheduledInterviewStatusFailure('An unknown error occurred.'));
    }
  }
}

function* handleCancelScheduledInterview(action: ReturnType<typeof actions.cancelScheduledInterview>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCancelScheduledInterview');
      return;
    }

    const { eventTypeId, eventKey, note } = action.payload;

    const { calendlyEvent } = yield call(
      api.toggleScheduledInterviewStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      { eventType: eventTypeId, eventKey, note },
      'cancel',
    );
    yield put(actions.toggleScheduledInterviewStatusSuccess(calendlyEvent, eventTypeId));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.toggleScheduledInterviewStatusFailure(err.message));
    } else {
      yield put(actions.toggleScheduledInterviewStatusFailure('An unknown error occurred.'));
    }
  }
}

function* handleFailScheduledInterview(action: ReturnType<typeof actions.failScheduledInterview>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleFailScheduledInterview');
      return;
    }

    const { eventTypeId, eventName, eventKey, note, rescind } = action.payload;

    const { calendlyEvent } = yield call(
      api.toggleScheduledInterviewStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      { eventType: eventTypeId, eventName, eventKey, note, undo: rescind },
      'fail',
    );
    yield put(actions.toggleScheduledInterviewStatusSuccess(calendlyEvent));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.toggleScheduledInterviewStatusFailure(err.message));
    } else {
      yield put(actions.toggleScheduledInterviewStatusFailure('An unknown error occurred.'));
    }
  }
}

function* handleResitScheduledInterview(action: ReturnType<typeof actions.resitScheduledInterview>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleResitScheduledInterview');
      return;
    }

    const { eventTypeId, eventName, eventKey, note, rescind, newCalendlyInterview, sourceComponentId } = action.payload;
    let calendlyEvent;

    ({ calendlyEvent } = yield call(
      api.toggleScheduledInterviewStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      { eventType: eventTypeId, eventName, eventKey, note, undo: rescind },
      'resit',
    ));

    if (newCalendlyInterview) {
      ({ calendlyEvent } = yield call(
        api.toggleScheduledInterviewStatus,
        idToken,
        student.core.university,
        student.core.student_id.split('#')[1],
        { eventType: newCalendlyInterview, note },
        'request',
      ));
    }

    yield put(
      actions.toggleScheduledInterviewStatusSuccess(
        calendlyEvent,
        rescind ? API_CALLS.FINGER_RESCIND_RESIT : API_CALLS.FINGER_RESIT,
        sourceComponentId,
      ),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.toggleScheduledInterviewStatusFailure(err.message));
    } else {
      yield put(actions.toggleScheduledInterviewStatusFailure('An unknown error occurred.'));
    }
  }
}

function* handlePassScheduledInterview(action: ReturnType<typeof actions.passScheduledInterview>): Generator {
  const { sourceComponentId } = action.payload;
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handlePassScheduledInterview');
      return;
    }

    const { eventTypeId, eventName, eventKey, status } = action.payload;

    const { calendlyEvent } = yield call(
      api.toggleScheduledInterviewStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      { eventType: eventTypeId, eventName, eventKey, status },
      'pass',
    );
    yield put(actions.toggleScheduledInterviewStatusSuccess(calendlyEvent, API_CALLS.FINGER_PASS, sourceComponentId));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.toggleScheduledInterviewStatusFailure(err.message, API_CALLS.FINGER_PASS, sourceComponentId));
    } else {
      yield put(
        actions.toggleScheduledInterviewStatusFailure(
          'An unknown error occurred.',
          API_CALLS.FINGER_PASS,
          sourceComponentId,
        ),
      );
    }
  }
}

function* handleSaveRecordedInterviewDocuments(
  action: ReturnType<typeof actions.saveRecordedInterviewDocuments>,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveRecordedInterviewDocuments');
      return;
    }
    const { interviewHash, attemptHash } = action.payload;

    const fileNames = [
      ...((student.offer as any).recordedInterviews?.[interviewHash]?.attempts?.[attemptHash]?.fileNames || []),
    ];

    const { offer } = yield call(
      api.saveRecordedInterviewDocs,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      interviewHash,
      attemptHash,
      fileNames,
    );
    return yield put(actions.saveRecordedInterviewDocumentsSuccess(offer));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveRecordedInterviewDocumentsFailure(err.message));
    } else {
      yield put(actions.saveRecordedInterviewDocumentsFailure('An unknown error occurred.'));
    }
  }
}

function* handleDeleteRecordedInterviewFile(action: ReturnType<typeof actions.deleteRecordedInterviewFile>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleDeleteRecordedInterviewFile');
      return;
    }
    const { interviewType, interviewHash, attemptHash } = action.payload;

    const body = {
      deletedFileName: action.payload.fileName,
      uploadedBy: action.payload.deletedBy,
    };

    const { offer } = yield call(
      api.deleteRecordedInterviewDocument,
      idToken,
      student.core.university,
      student.core.student_number,
      student.offer.intakeId,
      student.offer.offerId,
      interviewType,
      interviewHash,
      attemptHash,
      body,
    );

    return yield put(actions.deleteRecordedInterviewFileSuccess(offer, action.payload.fileName));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.deleteRecordedInterviewFileFailure(err.message));
    } else {
      yield put(actions.deleteRecordedInterviewFileFailure('An unknown error occurred.'));
    }
  }
}

function* handleFailRecordedInterview(action: ReturnType<typeof actions.failRecordedInterview>): Generator {
  const { sourceComponentId, rescind } = action.payload;
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleFailRecordedInterview');
      return;
    }

    const { interviewHash, note, rescind } = action.payload;

    yield call(
      api.toggleRecordedInterviewStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      { interviewHash, note, undo: rescind },
      'fail',
    );
    yield put(
      actions.toggleRecordedInterviewStatusSuccess({
        interviewHash,
        note,
        status: RecordedInterviewSystemStatus.FAILED,
        eventType: rescind ? API_CALLS.FINGER_RESCIND_FAIL : API_CALLS.FINGER_FAIL,
        undo: rescind,
        sourceComponentId,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(
        actions.toggleRecordedInterviewStatusFailure(
          err.message,
          rescind ? API_CALLS.FINGER_RESCIND_FAIL : API_CALLS.FINGER_FAIL,
          sourceComponentId,
        ),
      );
    } else {
      yield put(
        actions.toggleRecordedInterviewStatusFailure(
          'An unknown error occurred.',
          rescind ? API_CALLS.FINGER_FAIL : API_CALLS.FINGER_RESCIND_FAIL,
          sourceComponentId,
        ),
      );
    }
  }
}

function* handleResitRecordedInterview(action: ReturnType<typeof actions.resitRecordedInterview>): Generator {
  const { rescind, sourceComponentId, interviewHash, note, newRecordedInterviewId, newInterviewName } = action.payload;
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleResitRecordedInterview');
      return;
    }

    yield call(
      api.toggleRecordedInterviewStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      { interviewHash, note, undo: rescind, newRecordedInterviewId, newInterviewName },
      'resit',
    );
    yield put(
      actions.toggleRecordedInterviewStatusSuccess({
        interviewHash,
        note,
        status: RecordedInterviewSystemStatus.RESIT,
        eventType: rescind ? API_CALLS.FINGER_RESCIND_RESIT : API_CALLS.FINGER_RESIT,
        undo: rescind,
        sourceComponentId,
      }),
    );

    if (newRecordedInterviewId) {
      const { interviews } = yield call(
        api.requestStudentRecordedInterview,
        idToken,
        student.core.university,
        student.core.student_id.split('#')[1],
        student.offer.intakeId,
        student.offer.offerId,
        newRecordedInterviewId,
        note,
      );
      yield put(actions.onRequestRecordedInterviewSuccess(interviews));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(
        actions.toggleRecordedInterviewStatusFailure(
          err.message,
          rescind ? API_CALLS.FINGER_RESCIND_RESIT : API_CALLS.FINGER_RESIT,
          sourceComponentId,
        ),
      );
    } else {
      yield put(
        actions.toggleRecordedInterviewStatusFailure(
          'An unknown error occurred.',
          rescind ? API_CALLS.FINGER_RESCIND_RESIT : API_CALLS.FINGER_RESIT,
          sourceComponentId,
        ),
      );
    }
  }
}

function* handlePassRecordedInterview(action: ReturnType<typeof actions.passRecordedInterview>): Generator {
  const { sourceComponentId } = action.payload;
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handlePassRecordedInterview');
      return;
    }

    const { interviewHash, status } = action.payload;

    yield call(
      api.toggleRecordedInterviewStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      { interviewHash, status },
      'pass',
    );
    yield put(
      actions.toggleRecordedInterviewStatusSuccess({
        interviewHash,
        status,
        eventType: API_CALLS.FINGER_PASS,
        sourceComponentId,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.toggleRecordedInterviewStatusFailure(err.message, API_CALLS.FINGER_PASS, sourceComponentId));
    } else {
      yield put(
        actions.toggleRecordedInterviewStatusFailure(
          'An unknown error occurred.',
          API_CALLS.FINGER_PASS,
          sourceComponentId,
        ),
      );
    }
  }
}

function* handleSavePreCasInterview(action: ReturnType<typeof actions.savePreCasInterview>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSavePreCasInterview');
      return;
    }

    let cas;
    switch (action.payload) {
      case InterviewPages.RESET_INTERVIEW: {
        cas = yield call(
          api.resetInterview,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
        );
        break;
      }
      case InterviewPages.PAGE1: {
        const update: api.I1Update = {
          whyUk: student.preCasInterview.whyUk || '',
          whyUkOther: student.preCasInterview.whyUkOther || '',
          selectedCourseReasons: student.preCasInterview.selectedCourseReasons || [],
          selectedCourseReasonsOther: student.preCasInterview.selectedCourseReasonsOther || '',
          selectedUniReasons: student.preCasInterview.selectedUniReasons || [],
          selectedUniReasonsOther: student.preCasInterview.selectedUniReasonsOther || '',
          uniChoiceReason1: student.preCasInterview.uniChoiceReason1 || '',
          uniChoiceReason2: student.preCasInterview.uniChoiceReason2 || '',
          uniChoiceReason3: student.preCasInterview.uniChoiceReason3 || '',
        };
        cas = yield call(
          api.updateInterview,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'i-step1',
        );
        break;
      }
      case InterviewPages.PAGE2: {
        const update: api.I2Update = {
          campus: student.preCasInterview.campus || '',
          considerOtherUni: student.preCasInterview.considerOtherUni,
          otherUni1: student.preCasInterview.otherUni1 || '',
          otherUni2: student.preCasInterview.otherUni2 || '',
          selectedResearchMethods: student.preCasInterview.selectedResearchMethods!,
          selectedResearchMethodsOther: student.preCasInterview.selectedResearchMethodsOther || '',
          whyNotOtherUni: student.preCasInterview.whyNotOtherUni || '',
          considerOtherCourses: student.preCasInterview.considerOtherCourse === 'Yes',
          considerOtherCourse: student.preCasInterview.whyNotConsiderOtherCourse || '',
          whyNotConsiderOtherCourse: student.preCasInterview.whyNotConsiderOtherCourse || '',
          courseAlignFutureCareer: student.preCasInterview.courseAlignFutureCareer,
          courseAlignFutureCareerOther: student.preCasInterview.courseAlignFutureCareerOther || '',
        };

        cas = yield call(
          api.updateInterview,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'i-step2',
        );
        break;
      }

      case InterviewPages.PAGE3: {
        const update: api.I3Update = {
          workWhileStudy: student.preCasInterview.workWhileStudy,
          maxWorkHoursPerWeek: Number(student.preCasInterview.maxWorkHoursPerWeek),
          whyWorkWhileStudy: student.preCasInterview.whyWorkWhileStudy!,
          whyWorkWhileStudyOther: student.preCasInterview.whyWorkWhileStudyOther || '',
          affordCourseAndLivingWithoutWorking: student.preCasInterview.affordCourseAndLivingWithoutWorking,
          affordCourseAndLivingWithoutWorkingNo: student.preCasInterview.affordCourseAndLivingWithoutWorkingNo || '',
          completeDegreeWorkCountry: student.preCasInterview.completeDegreeWorkCountry || '',
          expectedSalary: Number(student.preCasInterview.expectedSalary),
          furtherStudyInUk: student.preCasInterview.furtherStudyInUk,
          furtherStudyCareerPlans: student.preCasInterview.furtherStudyCareerPlans || '',
        };
        cas = yield call(
          api.updateInterview,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'i-step3',
        );
        break;
      }
      case InterviewPages.PAGE4: {
        const update: api.I4Update = {
          liveWhileStudying: student.preCasInterview.liveWhileStudying || '',
          liveWhileStudyingOther: student.preCasInterview.liveWhileStudyingOther || '',
          expectedWeeklyRent: Number(student.preCasInterview.expectedWeeklyRent) || 0,
          commutePlan: student.preCasInterview.commutePlan || '',
          expectedLivingExpenses: Number(student.preCasInterview.expectedLivingExpenses) || 0,
          awareOfUKVIInterview: student.preCasInterview.awareOfUKVIInterview,
        };

        cas = yield call(
          api.updateInterview,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'i-step4',
        );
        break;
      }
    }
    yield put(actions.savePreCasInterviewSuccess(cas));
    yield call(handleNavigationAfterSave);
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.savePreCasInterviewFailure(err.message));
    } else {
      yield put(actions.savePreCasInterviewFailure('An unknown error occured.'));
    }
  }
}

function* handleSaveVisaPrepForm(action: ReturnType<typeof actions.saveVisaPrepForm>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleSaveVisaPrepForm');
      return;
    }

    let cas;
    switch (action.payload) {
      case VisaPages.VISA_UNLOCK: {
        cas = yield call(
          api.enableVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
        );
        break;
      }
      case VisaPages.PAGE1: {
        const passports: Array<api.Passport> = student.visa.additionalPassports!.map(passport => {
          return {
            docNumber: passport.docNumber || '',
            placeOfIssue: passport.placeOfIssue || '',
            issuingAuthority: passport.issuingAuthority || '',
            issueDate: passport.issueDate || '',
            expiryDate: passport.expiryDate || '',
          };
        });

        const update: api.V1Update = {
          hasAdditionalPassports: student.visa.hasAdditionalPassports!,
          additionalPassports: passports,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],

          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step1',
        );
        break;
      }
      case VisaPages.PAGE1_2: {
        const update: api.V12Update = {
          residentialAddressStartDate: student.visa.residentialAddressStartDate || '',
          homeOwnershipStatus: student.visa.homeOwnershipStatus || '',
          homeOwnershipStatusOther: student.visa.homeOwnershipStatusOther || '',
          hasDifferentContactDetails: student.visa.hasDifferentContactDetails,
          diffContactAddress: student.visa.diffContactAddress || '',
          diffContactCity: student.visa.diffContactCity || '',
          diffContactPostcode: student.visa.diffContactPostcode || '',
          diffContactCountry: student.visa.diffContactCountry || '',
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],

          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step1-2',
        );
        break;
      }
      case VisaPages.PAGE2_1:
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            nonHomeCountryApplicationDesc: student.visa.nonHomeCountryApplicationDesc || '',
          },
          'v-step2-1',
        );
        break;

      case VisaPages.PAGE2_2: {
        const otherUkVisas: Array<api.OtherUkVisas> = student.visa.otherUkVisas!.map(visa => {
          return {
            visaType: visa.visaType || '',
            issueDate: visa.issueDate || '',
            expiryDate: visa.expiryDate || '',
            country: visa.country || '',
            issuingAuthority: visa.issuingAuthority || '',
          };
        });

        const update: api.V22Update = {
          hasOtherUkVisas: student.visa.hasOtherUkVisas!,
          otherUkVisas,
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step2-2',
        );
        break;
      }
      case VisaPages.PAGE2_3: {
        const tripsToUk: Array<api.TripsToUK> = student.visa.tripsToUk!.map(trips => {
          return {
            arrivalDate: trips.arrivalDate || '',
            departureDate: trips.departureDate || '',
            purpose: trips.purpose || '',
          };
        });

        const update: api.V23Update = {
          tripsToUk,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step2-3',
        );
        break;
      }
      case VisaPages.PAGE2_4: {
        const exclusionOrders: Array<api.ExclusionOrders> = student.visa.exclusionOrders!.map(orders => {
          return {
            exclusionDate: orders.exclusionDate || '',
            reason: orders.reason,
            reference: orders.reference,
          };
        });

        const update: api.V24Update = {
          hasBeenToUk: student.visa.hasBeenToUk!,
          hasNINumber: student.visa.hasNINumber,
          niNumber: student.visa.niNumber || '',
          hasReceivedMedicalTreatment: student.visa.hasReceivedMedicalTreatment,
          hasVoluntarilyDeparted: student.visa.hasVoluntarilyDeparted,
          hasExclusionOrder: student.visa.hasExclusionOrder,
          exclusionOrders,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step2-4',
        );
        break;
      }
      case VisaPages.PAGE2_5: {
        const leaveToRemains: Array<api.LeaveToRemains> = student.visa.leaveToRemains!.map(leave => {
          return {
            applicationDate: leave.applicationDate || '',
            applicationType: leave.applicationType || '',
            reference: leave.reference || '',
          };
        });

        const update: api.V25Update = {
          leaveToRemains,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step2-5',
        );
        break;
      }
      case VisaPages.PAGE2_6: {
        const borderRefusals: Array<api.BorderRefusals> = student.visa.borderRefusals!.map(refusal => {
          return {
            reference: refusal.reference,
            refusalDate: refusal.refusalDate || '',
            airport: refusal.airport || '',
            reason: refusal.reason || '',
          };
        });

        const update: api.V26Update = {
          borderRefusals,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step2-6',
        );
        break;
      }
      case VisaPages.PAGE2_7: {
        const deportations: Array<api.Deportations> = student.visa.deportations!.map(deportation => {
          return {
            country: deportation.country || '',
            removalDate: deportation.removalDate || '',
            airport: deportation.airport || '',
            reason: deportation.reason || '',
          };
        });

        const update: api.V27Update = {
          hasBeenDeported: student.visa.hasBeenDeported!,
          deportations,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step2-7',
        );
        break;
      }
      case VisaPages.PAGE2_8: {
        const travelItems: Array<api.TravelItems> = student.visa.travelItems!.map(travelItem => {
          return {
            country: travelItem.country || '',
            reason: travelItem.reason || '',
            reasonOther: travelItem.reasonOther || '',
            entryDate: travelItem.entryDate || '',
            exitDate: travelItem.exitDate || '',
          };
        });

        const update: api.V28Update = {
          hasTravelled: student.visa.hasTravelled!,
          travelItems,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step2-8',
        );
        break;
      }
      case VisaPages.PAGE3_1:
        const update: api.V31Update = {
          maritalStatus: student.visa.maritalStatus || '',
          spouseNationality: student.visa.spouseNationality || '',
          spouseSameNationality: student.visa.spouseSameNationality,
          spouseFamilyName: student.visa.spouseFamilyName || '',
          spouseGivenName: student.visa.spouseGivenName || '',
          spouseDob: student.visa.spouseDob || '',
          spouseLivingWith: student.visa.spouseLivingWith,
          spouseTravellingWith: student.visa.spouseTravellingWith || '',
          spouseUkVisa: student.visa.spouseUkVisa,
          spouseHasPassport: student.visa.spouseHasPassport,
          spousePassportNumber: student.visa.spousePassportNumber || '',
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step3-1',
        );
        break;
      case VisaPages.PAGE3_2: {
        const update: api.V32Update = {
          hasFathersPersonalDetails: student.visa.hasFathersPersonalDetails!,
          fatherNationality: student.visa.fatherNationality || '',
          fatherSameNationality: student.visa.fatherSameNationality,
          fatherFamilyName: student.visa.fatherFamilyName || '',
          fatherGivenName: student.visa.fatherGivenName || '',
          fatherDob: student.visa.fatherDob || '',
          fatherTravellingWith: student.visa.fatherTravellingWith || '',
          fatherPlaceOfBirth: student.visa.fatherPlaceOfBirth || '',
          fatherUkVisa: student.visa.fatherUkVisa,
          fatherHasPassport: student.visa.fatherHasPassport,
          fatherPassportNumber: student.visa.fatherPassportNumber || '',
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step3-2',
        );
        break;
      }
      case VisaPages.PAGE3_3: {
        const update: api.V33Update = {
          hasMothersPersonalDetails: student.visa.hasMothersPersonalDetails!,
          motherNationality: student.visa.motherNationality || '',
          motherSameNationality: student.visa.motherSameNationality,
          motherFamilyName: student.visa.motherFamilyName || '',
          motherGivenName: student.visa.motherGivenName || '',
          motherDob: student.visa.motherDob || '',
          motherTravellingWith: student.visa.motherTravellingWith || '',
          motherPlaceOfBirth: student.visa.motherPlaceOfBirth || '',
          motherUkVisa: student.visa.motherUkVisa,
          motherHasPassport: student.visa.motherHasPassport,
          motherPassportNumber: student.visa.motherPassportNumber || '',
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step3-3',
        );
        break;
      }
      case VisaPages.PAGE3_4: {
        const update: api.V34Update = {
          hasGuardiansPersonalDetails: student.visa.hasGuardiansPersonalDetails!,
          guardianNationality: student.visa.guardianNationality || '',
          guardianSameNationality: student.visa.guardianSameNationality,
          guardianFamilyName: student.visa.guardianFamilyName || '',
          guardianGivenName: student.visa.guardianGivenName || '',
          guardianDob: student.visa.guardianDob || '',
          guardianTravellingWith: student.visa.guardianTravellingWith || '',
          guardianPlaceOfBirth: student.visa.guardianPlaceOfBirth || '',
          guardianUkVisa: student.visa.guardianUkVisa,
          guardianHasPassport: student.visa.guardianHasPassport,
          guardianPassportNumber: student.visa.guardianPassportNumber || '',
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step3-4',
        );
        break;
      }
      case VisaPages.PAGE3_5: {
        const children: Array<api.Children> = student.visa.children!.map(child => {
          return {
            nationality: child.nationality || '',
            gender: child.gender || '',
            sameNationality: child.sameNationality,
            familyName: child.familyName || '',
            givenName: child.givenName || '',
            dob: child.dob || '',
            placeOfBirth: child.placeOfBirth || '',
            travellingWith: child.travellingWith || '',
            ukVisa: child.ukVisa,
            hasPassport: child.hasPassport,
            passportNumber: child.passportNumber || '',
          };
        });

        const update = {
          hasDependantChildren: student.visa.hasDependantChildren!,
          children,
          childrenLivingWithYou: student.visa.childrenLivingWithYou,
          contactAddress: student.visa.contactAddress,
          contactCity: student.visa.contactCity,
          contactCountry: student.visa.contactCountry,
          contactPostcode: student.visa.contactPostcode,
          contactNumber: student.visa.contactNumber,
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step3-5',
        );
        break;
      }
      case VisaPages.PAGE4: {
        const otherTravellers: Array<api.OtherTravellers> = student.visa.otherTravellers!.map(traveller => {
          return {
            relationship: traveller.relationship || '',
            relationshipOther: traveller.relationshipOther || '',
            adultOrChild: traveller.adultOrChild || '',
            nationality: traveller.nationality || '',
            sameNationality: traveller.sameNationality,
            familyName: traveller.familyName || '',
            givenName: traveller.givenName || '',
            dob: traveller.dob,
            ukVisa: traveller.ukVisa,
            hasPassport: traveller.hasPassport,
            passportNumber: traveller.passportNumber || '',
          };
        });

        const update: api.V4Update = {
          travellingWithOthers: student.visa.travellingWithOthers!,
          otherTravellers,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step4',
        );
        break;
      }

      case VisaPages.PAGE5: {
        const family: Array<api.Family> = student.visa.familyMembers!.map(family => {
          return {
            nationality: family.nationality || '',
            familyName: family.familyName || '',
            givenName: family.givenName || '',
            dob: family.dob || '',
          };
        });

        const update: api.V5Update = {
          hasArrivalDate: student.visa.hasArrivalDate!,
          arrivalDate: student.visa.arrivalDate || '',
          familyContactAddress: student.visa.familyContactAddress || '',
          familyContactCity: student.visa.familyContactCity || '',
          familyContactCountry: student.visa.familyContactCountry || '',
          familyContactPostcode: student.visa.familyContactPostcode || '',
          familyPhoneNumber: student.visa.familyPhoneNumber || '',
          familyEmail: student.visa.familyEmail || '',
          family,
          hasOtherContactAddress: student.visa.hasOtherContactAddress!,
          lengthOfStayDays: Number(student.visa.lengthOfStayDays) || 0,
          lengthOfStayMonths: Number(student.visa.lengthOfStayMonths) || 0,
          lengthOfStayYears: Number(student.visa.lengthOfStayYears) || 0,
          otherContactAddress: student.visa.otherContactAddress || '',
          otherContactCity: student.visa.otherContactCity || '',
          otherContactCountry: student.visa.otherContactCountry || '',
          otherContactPostcode: student.visa.otherContactPostcode || '',
          otherPhoneNumber: student.visa.otherPhoneNumber || '',
          stayingWithFamily: student.visa.stayingWithFamily!,
          ukFamily: student.visa.ukFamily!,
          ukPresence: student.visa.ukPresence!,
        };
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step5',
        );
        break;
      }
      case VisaPages.PAGE6: {
        const update: api.V6Update = {
          hasCriminalCharges: student.visa.hasCriminalCharges!,
          hasTerrorismInvolvement: student.visa.hasTerrorismInvolvement!,
          hasTerrorismConnection: student.visa.hasTerrorismConnection!,
          hasTerrorismJustified: student.visa.hasTerrorismJustified!,
          hasWarCrimes: student.visa.hasWarCrimes!,
          hasBadActivities: student.visa.hasBadActivities!,
          language: student.visa.language || '',
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step6',
        );
        break;
      }
      case VisaPages.PAGE7_1: {
        const update: api.V71Update = {
          hasReceivedCas: student.visa.hasReceivedCas!,
          studiesConfirmationNumber: student.visa.studiesConfirmationNumber || '',
          courseEndDate: student.visa.courseEndDate || '',
          alreadyStartedStudying: student.visa.alreadyStartedStudying!,
          studyAddress: student.visa.studyAddress || '',
          assessedBySponsor: student.visa.assessedBySponsor!,
        };

        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          update,
          'v-step7-1',
        );
        break;
      }
      case VisaPages.PAGE7_2:
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            qualificationAndCountry: student.visa.qualificationAndCountry || '',
            qualificationAsProofOfEnglish: student.visa.qualificationAsProofOfEnglish!,
          },
          'v-step7-2',
        );
        break;
      case VisaPages.PAGE7_3:
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            appliedThroughUCAS: student.visa.appliedThroughUCAS!,
            ucasPin: student.visa.ucasPin || '',
          },
          'v-step7-3',
        );
        break;
      case VisaPages.PAGE8:
        cas = yield call(
          api.updateVisaPrep,
          idToken,
          student.core.university,
          student.core.student_id.split('#')[1],
          student.offer.intakeId,
          student.offer.offerId,
          {
            otherFeedback: student.visa.otherFeedback || '',
            difficultiesFeedback: student.visa.difficultiesFeedback || '',
          },
          'v-step8',
        );
        break;
    }
    yield put(actions.saveVisaPrepFormSuccess(cas));
    yield call(handleNavigationAfterSave);
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveVisaPrepFormFailure(err.message));
    } else {
      yield put(actions.saveVisaPrepFormFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateConflict(action: ReturnType<typeof actions.updateConflict>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateConflict');
      return;
    }

    const res: any = yield call(
      api.updateConflict,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      {
        id: action.payload.id,
        option: action.payload.option,
        index: action.payload.index,
      },
    );
    yield put(actions.updateConflictSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.updateConflictFailure(err.message));
    } else {
      yield put(actions.updateConflictFailure('An unknown error occured.'));
    }
  }
}

function* handleApproveAgentSection(): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      return;
    }

    const uni = student.core.university;
    const [, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;

    yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, { response: 'approve' }, 'agentApprove');

    yield put(actions.getStudentRecord(studentNo, uni, intakeId, offerId));
  } catch (err) {
    const message = err instanceof Error ? err.message : 'An unknown error occurred.';
    yield put(actions.approveAgentSectionFailure(message));
  }
}

function* handleSaveAgentDetails(action: ReturnType<typeof actions.saveAgentDetails>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      return;
    }

    const uni = student.core.university;
    const [, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;

    yield call(api.updateStudent, idToken, uni, studentNo, intakeId, offerId, action.payload, 'agent-details');

    yield put(actions.getStudentRecord(studentNo, uni, intakeId, offerId));
    yield call(handleNavigationAfterSave);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'An unknown error occurred.';
    yield put(actions.saveAgentDetailsFailure(message));
  }
}

function* handleOverrideMaturityDate(action: ReturnType<typeof actions.overrideMaturityDate>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleOverrideMaturityDate');
      return;
    }

    yield call(
      api.overrideMaturityDate,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload,
    );
  } catch (err) {
    console.log(err);
  }
}

function* handleUpdateAssignedAgents(action: ReturnType<typeof coreActions.updateAssignedAgents>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateAssignedAgents');
      return;
    }

    const res = yield call(
      api.assignAgents,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload,
    );
    yield put(coreActions.updateAssignedAgentsSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(coreActions.updateAssignedAgentsFailure(err.message));
    } else {
      yield put(coreActions.updateAssignedAgentsFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateUniStudentNumber(action: ReturnType<typeof actions.updateUniStudentNumber>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateUniStudentNumber');
      return;
    }

    yield call(
      api.updateUniStudentNumber,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.uniStudentNumber,
    );
  } catch (err) {
    console.log(err);
  }
}

function* handleUpdateOfferEvidence(action: ReturnType<typeof actions.updateOfferEvidence>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateOfferEvidence');
      return;
    }

    const res: any = yield call(
      api.updateOfferEvidence,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload.index,
      action.payload.value,
    );
    yield put(actions.updateOfferEvidenceSuccess(res));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.updateConflictFailure(err.message));
    } else {
      yield put(actions.updateConflictFailure('An unknown error occured.'));
    }
  }
}

function* handleUpdateStatus(action: ReturnType<typeof actions.updateStatus>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateStatus');
      return;
    }

    const res: any = yield call(
      api.updateStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,

      action.payload.status,
    );
    if (res) {
      yield put(actions.updateStatusSuccess(res));
    }
  } catch (err) {
    console.log(err);
  }
}

function* handleUpdateVisaStatus(action: ReturnType<typeof actions.updateVisaStatus>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateVisaStatus');
      return;
    }

    const res: any = yield call(
      api.updateVisaStatus,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload,
    );
    if (res) {
      yield put(actions.updateVisaStatusSuccess(res));
    }
  } catch (err) {
    console.log(err);
  }
}

function* handleUpdateArrivalStatus(action: ReturnType<typeof actions.updateArrivalStatus>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateArrivalStatus');
      return;
    }
    const studentNumber = student.core.student_id.split('#')[1];
    yield call(
      api.updateArrivalStatus,
      idToken,
      student.core.university,
      studentNumber,
      student.offer.intakeId,
      student.offer.offerId,
      action.payload,
    );
    if (action.payload.requestNewTravelTicket) {
      yield put(
        actions.getStudentRecord(studentNumber, student.core.university, student.offer.intakeId, student.offer.offerId),
      );
    }
  } catch (err) {
    console.log(err);
  }
}

function* handleGetStudentRecord(action: ReturnType<typeof actions.getStudentRecord>): Generator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetStudentRecord');
    }
    const res: any = yield call(
      api.getStudentById,
      idToken,
      action.payload.university,
      action.payload.student_number,
      action.payload.intakeId,
      action.payload.offerId,
    );
    if (res) {
      yield put(actions.getStudentRecordSuccess(res.student, res.config, res.uniConfig));
    }
  } catch (err: any) {
    yield put(actions.getStudentRecordFailure(err.message));
  }
}

function* handleGetStudentDocs(): Generator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: StudentState = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired');
    }
    const res: any = yield call(
      api.getStudentDocs,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
    );
    if (res) {
      yield put(actions.getStudentDocsSuccess(res.documents));
    }
  } catch (err: any) {
    yield put(actions.getStudentRecordFailure(err.message));
  }
}

function* handleGetAuditLog(action: ReturnType<typeof actions.getAuditLog>): Generator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired - handleGetAuditLog');
    }
    const res: Awaited<ReturnType<typeof api.getAuditlog>> = yield call(
      api.getAuditlog,
      idToken,
      action.payload.uni,
      action.payload.studentNo,
      action.payload.intakeId,
      action.payload.offerId,
    );
    if (res && 'audit' in res) {
      yield put(actions.getAuditLogSuccess({ auditlogs: (res as any).audit }));
    }
  } catch (err: any) {
    yield put(actions.getAuditLogFailure(err.message));
  }
}
function* handleGetStudentRecordedInterviews(): Generator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: StudentState = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired');
    }
    const res: any = yield call(
      api.getStudentRecordedInterviews,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
    );
    if (res) {
      yield put(actions.getStudentRecordedInterviewsSuccess(res.interviews));
    }
  } catch (err: any) {
    yield put(actions.getStudentRecordFailure(err.message));
  }
}

function* handleGetStudentCalendlyEvents(): Generator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: StudentState = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired');
    }
    const res: any = yield call(
      api.getStudentCalendlyEvents,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
    );
    if (res) {
      yield put(actions.getStudentCalendlyEventsSuccess(res));
    }
  } catch (err: any) {
    yield put(actions.getStudentCalendlyEventsFailure(err.message));
  }
}

function* handleGetStudentFinDocs(): Generator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);

    const student: StudentState = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired');
    }
    const res: any = yield call(
      api.getStudentFinDocs,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
    );
    if (res) {
      yield put(actions.getStudentFinDocsSuccess(res.financeDocuments));
    }
  } catch (err: any) {
    yield put(actions.getStudentRecordFailure(err.message));
  }
}
function* handleGetStudentMessages(): Generator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: StudentState = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired');
    }
    const res: any = yield call(
      api.getStudentMessages,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
    );
    if (res) {
      yield put(actions.getStudentMessagesSuccess(res.messages));
    }
  } catch (err: any) {
    yield put(actions.getStudentRecordFailure(err.message));
  }
}
function* handleGetStudentFollowUpDocs(): Generator {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const student: StudentState = yield select(getStudent);
    if (!idToken) {
      yield put(logout());
      throw new Error('Refresh token has expired');
    }
    const res: any = yield call(
      api.getStudentFollowUpDocs,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
    );
    if (res) {
      yield put(actions.getStudentFollowUpDocsSuccess(res.followUpDocuments));
    }
  } catch (err: any) {
    yield put(actions.getStudentRecordFailure(err.message));
  }
}

/** Websocket handling */
function* handleWebsocketMessage(action: any): Generator {
  const { action: serverAction, data } = JSON.parse(action.payload.data);
  const parsedData = JSON.parse(data);
  delay(1000); // only process ws every second
  const student: StudentState = yield select(getStudent);
  const ui: UIState = yield select(getUI);

  if (serverAction === 'updateCalendlyEvent') {
    yield put(actions.updateCalendlyEventWS(parsedData));
    // trigger re-fetch to get signed URLs once PDF processing is complete
    const leafNodeObj = getLeafNodeObj(parsedData, 'isProcessingPdf');
    if (leafNodeObj?.isProcessingPdf === false && ui.openModal === null) {
      yield put(actions.getStudentCalendlyEvents());
    }
    return;
  }

  const { intakeId: msgIntakeId, offerId: msgOfferId } = parsedData ?? {
    intakeId: undefined,
    offerId: undefined,
  };
  const { intakeId, offerId } = student?.offer ?? { intakeId: undefined, offerId: undefined };

  // note: DOCUMENTS record in not offer specific
  if (intakeId !== msgIntakeId || (serverAction !== 'updateDocuments' && offerId !== msgOfferId)) {
    console.log('mismatched intake / offer', intakeId, offerId, msgIntakeId, msgOfferId);
    return;
  }

  switch (serverAction) {
    // TODO remove these unused ws events
    // case 'newMessage':
    //   yield put(actions.newMessage(message.payload));
    //   break;
    // case 'updateCore':
    //   yield put(actions.updateCore(message.payload));
    //   break;
    // case 'updateStudentDetails':
    //   yield put(actions.updateStudentDetails(message.payload));
    //   break;
    // case 'updateHistory':
    //   yield put(actions.updateHistory(message.payload));
    //   break;
    // case 'updateFinance':
    //   yield put(actions.updateFinance(message.payload));
    //   break;
    case 'updateRecordedInterviews': {
      break;
    }
    case 'updateRecordedInterviewsDoc': {
      yield put(actions.updateRecordedInterviewWSDoc(parsedData.offer));
      // trigger event to get signed URLs, if modal has already been closed and processing complete
      const leafNodeObj = getLeafNodeObj(parsedData.offer, 'isProcessingPdf');
      if (leafNodeObj?.isProcessingPdf === false && ui.openModal === null) {
        const studentNumber = student.core.student_id.split('#')[1];
        yield put(
          actions.getStudentRecord(
            studentNumber,
            student.core.university,
            student.offer.intakeId,
            student.offer.offerId,
          ),
        );
      }
      break;
    }
    case 'updateSummary':
      yield put(actions.updateSummary(parsedData));
      break;
    case 'updateDocuments': {
      yield put(actions.updateDocuments(parsedData));
      // trigger event to get signed URLs, if modal has already been closed and processing complete
      const leafNodeObj = getLeafNodeObj(parsedData, 'isProcessingPdf');
      if (leafNodeObj?.isProcessingPdf === false && ui.openModal === null) {
        yield put(actions.getStudentDocs());
      }
      break;
    }
    case 'updateFinanceDocuments': {
      const { intakeId, offerId, finDocs } = parsedData;

      if (student.offer.intakeId === intakeId && student.offer.offerId === offerId) {
        yield put(actions.updateFinanceDocuments({ finDocs }));
        // trigger event to get signed URLs, if modal has already been closed and processing complete
        const leafNodeObj = getLeafNodeObj(parsedData, 'isProcessingPdf');
        if (leafNodeObj?.isProcessingPdf === false && ui.openModal === null) {
          yield put(actions.getStudentFinDocs());
        }
      }
      break;
    }
    case 'updateFollowUpDocuments': {
      const { intakeId, offerId, followUpDocs } = parsedData;

      if (student.offer.intakeId === intakeId && student.offer.offerId === offerId) {
        yield put(actions.updateFollowUpDocuments({ followUpDocs }));
        // trigger event to get signed URLs, if modal has already been closed and processing complete
        const leafNodeObj = getLeafNodeObj(parsedData, 'isProcessingPdf');
        if (leafNodeObj?.isProcessingPdf === false && ui.openModal === null) {
          yield put(actions.getStudentFollowUpDocs());
        }
      }
      break;
    }
    default:
      break;
  }
}

function* handleRegisterStudent(): Generator {
  try {
    const websocketState: WebsocketState = yield select(getWebsocket);

    if (!websocketState.connected) {
      yield take(['CONNECTION_SUCCESS']);
    }
    const currentUser: AuthState = yield select(getAuth);
    const student: StudentState = yield select(getStudent);
    const viewUni = currentUser.isPathway ? currentUser.uni : student.core.university;
    const message: IWebsocketServerMessage = {
      action: 'openStudent',
      channel: CHANNEL.STUDENT_LIST,
      view: `${viewUni}#${student.summary.intakeId}`,
      topic: TOPIC.OPEN_STUDENT,
      subject: student.core.student_number,
      data: JSON.stringify({
        uni: student.core.university,
        status: student.offer.status,
        user: currentUser.student_number,
      }),
    };
    yield put(websocketActions.send({ ...message }));

    // Re-subscribe to STUDENT_LIST/OPEN_STUDENT to get current state of who else has this student open
    const studentListSubKey = {
      channel: CHANNEL.STUDENT_LIST,
      view: `${viewUni}#${student.summary.intakeId}`,
      topic: [TOPIC.OPEN_STUDENT],
    };
    yield put(
      websocketActions.subscribeToWebsocket({
        key: JSON.stringify(studentListSubKey),
        handler: handleOpenedStudentRecords,
      }),
    );

    // subscribe to student websocket events
    const studentSubKey = {
      channel: CHANNEL.STUDENT,
      view: `${student.core.university}#${student.summary.intakeId}`,
      topic: [
        TOPIC.CALENDLY_INTERVIEWS,
        TOPIC.CALENDLY_INTERVIEW_DOCS,
        TOPIC.DOCUMENTS,
        TOPIC.FINANACE_DOCUMENTS,
        TOPIC.FOLLOW_UP_DOCUMENTS,
        TOPIC.MESSAGES,
        TOPIC.RECORDED_INTERVIEWS,
        TOPIC.RECORDED_INTERVIEW_DOCS,
        TOPIC.STUDENT_CORE,
        TOPIC.STUDENT_DETAILS,
        TOPIC.STUDENT_FINANCE,
      ],
      subject: student.core.student_number,
    };
    yield delay(1000);
    yield put(
      websocketActions.subscribeToWebsocket({
        key: JSON.stringify(studentSubKey),
        handler: actions.handleWebsocketStudentRecords,
      }),
    );
  } catch (err) {
    console.log(`failed to register student`, err);
  }
}

function* handleStudentClosed(_action: any): Generator {
  const currentUser: AuthState = yield select(getAuth);
  const student: StudentState = yield select(getStudent);
  const viewUni = currentUser.isPathway ? currentUser.uni : student.core.university;
  const message: IWebsocketServerMessage = {
    action: 'closeStudent',
    channel: CHANNEL.STUDENT_LIST,
    view: `${viewUni}#${student.summary.intakeId}`,
    topic: TOPIC.OPEN_STUDENT,
    subject: student.core.student_number,
    data: JSON.stringify({
      status: student.offer.status,
      user: currentUser.student_number,
    }),
  };
  yield put(websocketActions.send({ ...message }));

  // unsubscribe from student websocket events
  const studentSubKey = {
    channel: CHANNEL.STUDENT,
    view: `${student.core.university}#${student.summary.intakeId}`,
    topic: [
      TOPIC.CALENDLY_INTERVIEWS,
      TOPIC.CALENDLY_INTERVIEW_DOCS,
      TOPIC.DOCUMENTS,
      TOPIC.FINANACE_DOCUMENTS,
      TOPIC.FOLLOW_UP_DOCUMENTS,
      TOPIC.MESSAGES,
      TOPIC.RECORDED_INTERVIEWS,
      TOPIC.RECORDED_INTERVIEW_DOCS,
      TOPIC.STUDENT_CORE,
      TOPIC.STUDENT_DETAILS,
      TOPIC.STUDENT_FINANCE,
    ],
    subject: student.core.student_number,
  };
  yield delay(1000);
  yield put(
    websocketActions.unsubscribeFromWebsocket({
      key: JSON.stringify(studentSubKey),
    }),
  );
}

/**End websocket handling */

function* handleNavigationAfterSave(): Generator {
  yield put(setNavigateStudentRecordAction(true));
}

function* handleDownloadAttachments(action: ReturnType<typeof actions.downloadAttachments>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleDownloadAttachments');
      return;
    }
    yield call(
      api.requestAttachmentsPacking,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
      action.payload?.includeHidden ?? false,
    );

    toast.warn('Your download will start shortly');
    const INITIAL_DELAY = 5000;
    const MAX_RETRIES = 100;
    const RETRY_DELAY = 15000;
    // initial delay to allow for the server to do a clean up
    yield delay(INITIAL_DELAY);

    const res = yield retry(MAX_RETRIES, RETRY_DELAY, function* () {
      const response = yield call(
        api.getAttachmentsUrl,
        idToken,
        student.core.university,
        student.core.student_id.split('#')[1],
        student.offer.intakeId,
        student.offer.offerId,
      );
      if (!response || !response.url) {
        throw new Error('Failed to get attachments URL');
      }
      return response;
    });

    yield put(actions.downloadAttachmentsSuccess(res.url));
  } catch (err) {
    yield put(actions.downloadAttachmentsFailure(err));
  }
}

function* handleDeleteStudent(_action: any): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleDeleteStudent');
      return;
    }
    yield call(
      api.deleteStudent,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.offer.intakeId,
      student.offer.offerId,
    );
    yield put(actions.deleteStudentSuccess(student.core.student_id.split('#')[1]));
  } catch (err: any) {
    console.error(err);
    yield put(actions.deleteStudentFailure(err.message));
  }
}

function* handleMarkStudentActive(_action: any): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleMarkStudentActive');
      return;
    }
    yield call(
      api.markStudentActive as any,
      idToken,
      student.core.university,
      student.core.student_id.split('#')[1],
      student.core.email,
      student.core.pathway,
    );
  } catch (err) {
    console.error(err);
  }
}

function* handleRemoveImportHash(): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRemoveImportHash');
      return;
    }
    yield call(api.removeImportHash, idToken, student.core.university, student.core.student_id.split('#')[1]);
    yield put(actions.removeImportHashSuccess());
  } catch (err: any) {
    console.error(err);
    yield put(actions.removeImportHashFailure(err.message));
  }
}

function* handleRevokeStudentAccess(_action: any): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRevokeStudentAccess');
      return;
    }
    yield call(api.revokeStudentAccess, idToken, student.core.university, student.core.email, student.core.pathway);
  } catch (err) {
    console.error(err);
  }
}

function* handleChangeStudentNo(action: any): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleChangeStudentNo');
      return;
    }

    // Validate essential student data before making API call
    const { university, student_id: studentId } = student.core;
    const { intakeId, offerId } = student.offer;
    const [, studentNumber] = studentId.split('#');

    if (!university || !studentId || !intakeId || !offerId || !studentNumber) {
      const errorMessage = `Error: Missing essential info to perform this task`;
      console.error('handleChangeStudentNo validation failed:', errorMessage);
      yield put(actions.changeStudentNoFailure(errorMessage));
      return;
    }

    yield call(api.changeStudentNo, idToken, university, studentNumber, action.payload, intakeId, offerId);
    yield put(actions.changeStudentNoSuccess(action.payload));
  } catch (err: any) {
    console.error(err);
    yield put(actions.changeStudentNoFailure(err.message));
  }
}

function* handleUpdateVisaRefusalMetaData(action: ReturnType<typeof docActions.updateVisaRefusalMetaData>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateVisaRefusalMetaData');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { sponsorType, sponsorKey, docKey, metadata } = action.payload;

    const body = {
      ...metadata,
    };
    const docs = yield call(
      api.updateVisaRefusalNoDoc,
      idToken,
      uni,
      studentNo,
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docKey!,
      body,
    );
    yield put(docActions.updateVisaRefusalMetaDataSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(docActions.updateVisaRefusalMetaDataFailure(err.message));
    } else {
      yield put(docActions.updateVisaRefusalMetaDataFailure('An unknown error occurred.'));
    }
  }
}

function* handleUpdateTBTestNoDoc(action: ReturnType<typeof docActions.updateTBTestMetaData>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleUpdateTBTestNoDoc');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;

    const { docKey, metadata } = action.payload;

    const body = {
      ...metadata,
    };

    const docs: any = yield call(api.updateTBTestNoDoc, idToken, uni, studentNo, intakeId, offerId, docKey!, body);
    yield put(docActions.updateTBSuccess(docs));
  } catch (err) {
    if (err instanceof Error) {
      yield put(docActions.updateTBFailure(err.message));
    } else {
      yield put(docActions.updateTBFailure('An unknown error occured.'));
    }
  }
}

function* handleNoBiometricsDoc(action: ReturnType<typeof actions.saveDocuments>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleNoBiometricsDoc');
      return;
    }
    const [uni, studentNo] = student.core.student_id.split('#');
    const { sponsorType, sponsorKey, docType, docKey } = action.payload;

    const doc = {
      ...student.documents[sponsorType][sponsorKey!].biometric![docKey!],
      hasDocument: false,
      status: 'SKIPPED',
    };

    const body = {
      ...doc,
    };

    const response = yield call(
      api.saveDocs,
      idToken,
      uni,
      studentNo,
      student.offer.intakeId,
      student.offer.offerId,
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      body,
    );
    yield put(
      actions.saveDocumentsSuccess({
        docs: response.documents,
        complete: response.complete,
        required: response.required,
      }),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.saveDocumentsFailure(err.message));
    } else {
      yield put(actions.saveDocumentsFailure('An unknown error occurred.'));
    }
  }
}

function* handleCancelRecordedInterview(action: ReturnType<typeof actions.cancelRecordedInterview>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleCancelRecordedInterview');
      return;
    }

    const [uni, studentNo] = student.core.student_id.split('#');
    const { intakeId, offerId } = student.offer;
    const { interviewHash, note } = action.payload;

    const { interviews } = yield call(
      api.cancelRecordedInterview,
      idToken,
      uni,
      studentNo,
      intakeId,
      offerId,
      interviewHash,
      note,
    );
    yield put(actions.cancelRecordedInterviewSuccess(interviews));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.cancelRecordedInterviewFailure(err.message));
    } else {
      yield put(actions.cancelRecordedInterviewFailure('An unknown error occurred.'));
    }
  }
}

function* handleScoresRegeneration(action: ReturnType<typeof actions.requestScoresRegeneration>): Generator {
  try {
    const { videoId } = action.payload;
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleScoresRegeneration');
      return;
    }

    const [uni, studentNo] = student.core.student_id.split('#');

    yield call(api.triggerScoresRegeneration, idToken, uni, studentNo, videoId);
  } catch (e) {
    yield put(actions.requestScoresRegenerationFailed(e));
  }
}

function* handleRecordedInterviewAssessment(
  action: ReturnType<typeof offerActions.assessRecordedInterview>,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRecordedInterviewAssessment');
      return;
    }

    const [uni, studentNo] = student.core.student_id.split('#');

    const { interviewHash, payload } = action.payload;

    const response: Record<string, IRecordedInterviewEntry> = yield call(
      api.assesssRecordedInterview,
      idToken,
      uni,
      studentNo,
      interviewHash,
      payload,
    );
    const interviewHashKey = Object.keys(response)[0];
    yield put(
      offerActions.assessRecordedInterviewSuccess({
        interviewHash: interviewHashKey,
        interview: response[interviewHashKey],
      }),
    );
  } catch (err) {
    put(offerActions.recordedInterviewEditQuestionScoreFailure(err));
  }
}

function* handleRecordedInterviewEditQuestionScore(
  action: ReturnType<typeof offerActions.recordedInterviewEditQuestionScore>,
): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleRecordedInterviewEditQuestionScore');
      return;
    }

    const [uni, studentNo] = student.core.student_id.split('#');

    const { interviewHash, payload } = action.payload;

    const response: Record<string, IRecordedInterviewEntry> = yield call(
      api.editQuestionScoreRecordedInterview,
      idToken,
      uni,
      studentNo,
      interviewHash,
      payload,
    );
    const interviewHashKey = Object.keys(response)[0];
    yield put(
      offerActions.recordedInterviewEditQuestionScoreSuccess({
        interviewHash: interviewHashKey,
        interview: response[interviewHashKey],
      }),
    );
  } catch (err) {
    yield put(offerActions.recordedInterviewEditQuestionScoreFailure(err));
  }
}

function* handlVideoProcessingStatus(action: ReturnType<typeof actions.requestVideoProcessingStatus>): Generator {
  try {
    const student: StudentState = yield select(getStudent);
    const [uni, studentNo] = student.core.student_id.split('#');
    const { videoId, interviewHash } = action.payload;
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handlVideoProcessingStatus');
      return;
    }

    const res: IRecordedInterviewProcessingStatus = yield call(
      api.getVideoProcessingStatus,
      idToken,
      uni,
      studentNo,
      interviewHash,
      videoId,
    );
    yield put(actions.requestVideoProcessingStatusSuccess(res));
  } catch (e) {
    yield put(actions.requestVideoProcessingStatusFailed(e));
  }
}

function* handleOfferCalendlyInterviewInfoStatusUpdate(
  _action: ReturnType<typeof actions.toggleScheduledInterviewStatusSuccess>,
): Generator {
  const calendlyEvents: ICalendlyEvent[] = yield select((state: ApplicationState) => state.student.calendlyEvents);
  const calendlyInterviewsInfoStatus = getCalendlyInterviewsInfoStatus(calendlyEvents);

  yield put(offerActions.updateCalendlyInterviewsInfoStatus({ infoStatus: calendlyInterviewsInfoStatus }));
}

function* watchGetStudentRecord(): Generator {
  yield takeEvery(StudentActionTypes.GET_STUDENT_RECORD, handleGetStudentRecord);
}
function* watchGetStudentDocs(): Generator {
  yield takeEvery(StudentActionTypes.GET_STUDENT_DOCS, handleGetStudentDocs);
}
function* watchGetStudentFinDocs(): Generator {
  yield takeEvery(StudentActionTypes.GET_STUDENT_FIN_DOCS, handleGetStudentFinDocs);
}
function* watchGetStudentFollowUpDocs(): Generator {
  yield takeEvery(StudentActionTypes.GET_STUDENT_FOLLOW_UP_DOCS, handleGetStudentFollowUpDocs);
}
function* watchGetStudentRecordedInterviews(): Generator {
  yield takeEvery(StudentActionTypes.GET_STUDENT_RECORDED_INTERVIEWS, handleGetStudentRecordedInterviews);
}

function* watchGetStudentCalendlyEvents(): Generator {
  yield takeEvery(StudentActionTypes.GET_STUDENT_CALENDLY_EVENTS, handleGetStudentCalendlyEvents);
}

function* watchGetStudentMessages(): Generator {
  yield takeEvery(StudentActionTypes.GET_STUDENT_MESSAGES, handleGetStudentMessages);
}

function* watchGetAuditlog(): Generator {
  yield takeEvery(actions.getAuditLog.type, handleGetAuditLog);
}

function* watchSavePersonalInfo(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_PERSONAL_INFO, handleSavePersonalInfo);
}

function* watchSaveHistory(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_HISTORY, handleSaveHistory);
}

function* watchSaveConsents(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_CONSENTS, handleSaveConsents);
}

function* watchSaveVisaPrepForm(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_VISA_PREP_FORM, handleSaveVisaPrepForm);
}

function* watchSaveInterviewDocuments(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_INTERVIEW_DOCUMENTS, handleSaveInterviewDocuments);
}

function* watchSaveRecordedInterviewDocuments(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_RECORDED_INTERVIEW_DOCUMENTS, handleSaveRecordedInterviewDocuments);
}

function* watchRequestInterview(): Generator {
  yield takeEvery(StudentActionTypes.REQUEST_SCHEDULED_INTERVIEW, handleRequestScheduledInterview);
}

function* watchRequestRecordedInterview(): Generator {
  yield takeEvery(StudentActionTypes.REQUEST_RECORDED_INTERVIEW, handleRequestRecordedInterview);
}

function* watchCancelInterview(): Generator {
  yield takeEvery(StudentActionTypes.CANCEL_SCHEDULED_INTERVIEW, handleCancelScheduledInterview);
}

function* watchCancelRecordedInterview(): Generator {
  yield takeEvery(StudentActionTypes.CANCEL_RECORDED_INTERVIEW, handleCancelRecordedInterview);
}

function* watchFailInterview(): Generator {
  yield takeEvery(StudentActionTypes.FAIL_SCHEDULED_INTERVIEW, handleFailScheduledInterview);
}

function* watchResitInterview(): Generator {
  yield takeEvery(StudentActionTypes.RESIT_SCHEDULED_INTERVIEW, handleResitScheduledInterview);
}

function* watchPassInterview(): Generator {
  yield takeEvery(StudentActionTypes.PASS_SCHEDULED_INTERVIEW, handlePassScheduledInterview);
}

function* watchFailRecordedInterview(): Generator {
  yield takeEvery(StudentActionTypes.FAIL_RECORDED_INTERVIEW, handleFailRecordedInterview);
}

function* watchResitRecordedInterview(): Generator {
  yield takeEvery(StudentActionTypes.RESIT_RECORDED_INTERVIEW, handleResitRecordedInterview);
}

function* watchPassRecordedInterview(): Generator {
  yield takeEvery(StudentActionTypes.PASS_RECORDED_INTERVIEW, handlePassRecordedInterview);
}

function* watchSavePCI(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_PRE_CAS_INTERVIEW, handleSavePreCasInterview);
}

function* watchSaveDocuments(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_DOCUMENTS, handleSaveDocuments);
}

function* watchSaveDocumentWithFiles(): Generator {
  yield takeEvery(DocActionTypes.SAVE_DOCUMENT, handleSaveDocumentWithFiles);
}

function* handleSaveInternalDocumentWithFiles(action: ReturnType<typeof docActions.saveInternalDocument>): Generator {
  const context = yield getContext('sagaContext');
  const idToken = (yield call(context.getIdToken)) as string;
  if (!idToken) {
    yield put(logout());
    yield call(context.onLogout);
    return;
  }

  const { uni, applicantId, offerId, requirementId, docKey, fileNames, s3Keys, newS3Keys, removedS3Keys } =
    action.payload;

  // Phase 1: link files to the requirement. This is the source of truth for display.
  // If this fails nothing is persisted, which is the safe failure mode.
  // On failure, best-effort cleanup of newly uploaded S3 objects to avoid orphans.
  let response;
  let updateError: string | null = null;

  try {
    response = yield call(api.updateRequirementInternalData, {
      idToken,
      uni,
      applicantId,
      offerId,
      requirementId,
      body: { s3Keys, fileNames },
    });
    if (api.isErrorResponse(response)) {
      updateError = 'Failed to update requirement internal data';
    }
  } catch (err) {
    updateError = err instanceof Error ? err.message : 'Failed to update requirement internal data';
  }

  if (updateError) {
    yield put(docActions.saveInternalDocumentFailure(updateError));
    if (newS3Keys?.length) {
      for (const s3Key of newS3Keys) {
        try {
          yield call(api.deleteStaffInternalDocument, { idToken, uni, applicantId, docKey, s3Key });
        } catch {
          // best-effort — ignore cleanup errors
        }
      }
    }
    return;
  }

  yield put(docActions.saveInternalDocumentSuccess({ internalData: response.internalData }));

  try {
    const rootState: ApplicationState = yield select();
    const allTasks = getAllTasksFromRequirements(rootState.apply.offer.requirements);
    const currentTask = allTasks.find(task => task.id === requirementId);

    if (currentTask) {
      // Preserve signed URLs for keys that survive in the new snapshot.
      // response.internalData has no urls; getApplicantOffer will replace them all.
      const existingUrlMap = new Map<string, string>(
        (currentTask.internalData?.s3Keys ?? []).map((key, i) => [key, currentTask.internalData?.urls?.[i] ?? '']),
      );
      const mergedUrls = response.internalData.s3Keys
        .map((key: string) => existingUrlMap.get(key) ?? '')
        .filter((url: string) => url !== '');

      // Only do an immediate optimistic update when all s3Keys already have signed URLs
      // (i.e. deletion-only, no new uploads). When new files are present, mergedUrls will be
      // shorter than s3Keys because the new keys have no signed URL yet. In that case, skip
      // the partial update and let getApplicantOffer supply the complete, correct picture.
      if (mergedUrls.length === response.internalData.s3Keys.length) {
        const updatedTask = {
          ...currentTask,
          internalData: {
            ...response.internalData,
            urls: mergedUrls,
          },
        };
        yield put(updateApplicantRequirementStatusSuccess(updatedTask));
      }
    }
  } catch {
    // non-blocking apply-store sync issue; full state is refreshed via getApplicantOffer
  }

  // Re-fetch the offer so addUrlsToRequirement generates signed URLs for internalData.
  yield put(getApplicantOffer(applicantId, uni, offerId));

  // Phase 2: best-effort S3 cleanup for files removed in the modal.
  if (removedS3Keys?.length) {
    for (const s3Key of removedS3Keys) {
      try {
        yield call(api.deleteStaffInternalDocument, { idToken, uni, applicantId, docKey, s3Key });
      } catch {
        toast.warn('A previously uploaded file could not be fully removed. Please refresh and try again.');
      }
    }
  }
}

function* watchSaveInternalDocumentWithFiles(): Generator {
  yield takeLatest(DocActionTypes.SAVE_INTERNAL_DOCUMENT, handleSaveInternalDocumentWithFiles);
}

function* watchDeleteDocumentFromRequirement(): Generator {
  yield takeEvery(DocActionTypes.DELETE_DOCUMENT, handleDeleteDocument);
}

function* watchSaveFinanceDocument(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_FINANCE_DOCUMENTS, handleSaveFinanceDocuments);
}

function* watchSaveFollowUpDocument(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS, handleSaveFollowUpDocuments);
}

function* watchSaveFollowUpDocumentsShareCode(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS_SHARE_CODE, handleSaveFollowUpDocumentsShareCode);
}

function* watchSaveRisks(): Generator {
  yield takeEvery(StudentActionTypes.UPDATE_RISKS_REQUEST, handleSaveRisks);
}

function* watchRequestCustomDocument(): Generator {
  yield takeEvery(DocActionTypes.CUSTOM_DOCUMENT_REQUEST, handleCustomDocumentRequest);
}

function* watchRequestFinanceCustomDocument(): Generator {
  yield takeEvery(FinDocActionTypes.CUSTOM_DOCUMENT_REQUEST, handleFinanceCustomDocumentRequest);
}

function* watchRequestFollowUpCustomDocument(): Generator {
  yield takeEvery(FollowUpDocActionTypes.CUSTOM_DOCUMENT_REQUEST, handleFollowUpCustomDocumentRequest);
}

function* watchWSMessage(): Generator {
  yield takeEvery(StudentActionTypes.STUDENT_WEBSOCKET_CHANGE, handleWebsocketMessage);
}

function* watchApproveDocument(): Generator {
  yield takeEvery(DocActionTypes.APPROVE_DOC, handleToggleDocument);
}

function* watchRejectDocument(): Generator {
  yield takeEvery(DocActionTypes.REJECT_DOC, handleToggleDocument);
}

//rescindRejectDoc
function* watchRescindRejectDocument(): Generator {
  yield takeEvery(DocActionTypes.RESCIND_REJECT_DOC, handleToggleDocument);
}

function* watchDeleteDocument(): Generator {
  yield takeEvery(DocActionTypes.DELETE_DOC, handleToggleDocument);
}

function* watchNoBiometrics(): Generator {
  yield takeEvery(DocActionTypes.NO_BIOMETRICS, handleNoBiometricsDoc);
}

function* watchUpdateVisaRefusalMetadata(): Generator {
  yield takeEvery(DocActionTypes.UPDATE_VISA_REFUSAL_META_DATA, handleUpdateVisaRefusalMetaData);
}

function* watchUpdateTBTestDocMeta(): Generator {
  yield takeEvery(DocActionTypes.UPDATE_TB_TEST_META, handleUpdateTBTestNoDoc);
}

function* watchAcknowledgeDocument(): Generator {
  yield takeEvery(DocActionTypes.ACKNOWLEDGE_DOC, handleToggleDocument);
}

function* watchApproveFinDocument(): Generator {
  yield takeEvery(FinDocActionTypes.APPROVE_DOC, handleToggleFinanceDocument);
}

function* watchRejectFinDocument(): Generator {
  yield takeEvery(FinDocActionTypes.REJECT_DOC, handleToggleFinanceDocument);
}

function* watchRescindRejectFinDocument(): Generator {
  yield takeEvery(FinDocActionTypes.RESCIND_REJECT_DOC, handleToggleFinanceDocument);
}

function* watchApproveFollowUpDocument(): Generator {
  yield takeEvery(FollowUpDocActionTypes.APPROVE_DOC, handleToggleFollowUpDocument);
}

function* watchRejectFollowUpDocument(): Generator {
  yield takeEvery(FollowUpDocActionTypes.REJECT_DOC, handleToggleFollowUpDocument);
}

function* watchRescindRejectFollowUpDocument(): Generator {
  yield takeEvery(FollowUpDocActionTypes.RESCIND_REJECT_DOC, handleToggleFollowUpDocument);
}

function* watchDeleteFollowUpDocument(): Generator {
  yield takeEvery(FollowUpDocActionTypes.DELETE_DOC, handleToggleFollowUpDocument);
}

function* watchDeleteFinDocument(): Generator {
  yield takeEvery(FinDocActionTypes.DELETE_DOC, handleToggleFinanceDocument);
}

function* watchAcknowledgeFinDocument(): Generator {
  yield takeEvery(FinDocActionTypes.ACKNOWLEDGE_DOC, handleToggleFinanceDocument);
}

function* watchUpdateAssignedAgents(): Generator {
  yield takeEvery(CoreActionTypes.UPDATE_ASSIGNED_AGENTS, handleUpdateAssignedAgents);
}

function* watchUpdateUniStudentNumber(): Generator {
  yield takeEvery(StudentActionTypes.UPDATE_UNI_STUDENT_NUMBER, handleUpdateUniStudentNumber);
}

function* watchUpdateOfferEvidence(): Generator {
  yield takeEvery(StudentActionTypes.UPDATE_OFFER_EVIDENCE, handleUpdateOfferEvidence);
}

function* watchUpdateStatus(): Generator {
  yield takeEvery(StudentActionTypes.UPDATE_STATUS, handleUpdateStatus);
}

function* watchUpdateVisaStatus(): Generator {
  yield takeEvery(CoreActionTypes.UPDATE_VISA_STATUS, handleUpdateVisaStatus);
}

function* watchUpdateArrivalStatus(): Generator {
  yield takeEvery(EnrolmentStatusActionTypes.UPDATE_ARRIVAL_STATUS, handleUpdateArrivalStatus);
}

function* watchOverrideMaturityDate(): Generator {
  yield takeEvery(StudentActionTypes.OVERRIDE_MATURED_DATE, handleOverrideMaturityDate);
}

function* watchUpdateMessage(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_MESSAGE, handleSaveMessage);
}

function* watchDownloadAttachments(): Generator {
  yield takeEvery(StudentActionTypes.DOWNLOAD_ATTACHMENTS, handleDownloadAttachments);
}

function* watchSaveFinance(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_DEPOSIT, handleSaveDeposit);

  yield takeEvery(StudentActionTypes.CREATE_ORGANISATION_SPONSOR, handleCreateOrganisationSponsor);
  yield takeEvery(StudentActionTypes.UPDATE_ORGANISATION_SPONSOR, handleUpdateOrganisationSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_ORGANISATION_SPONSOR, handleRemoveOrganisationSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_ORGANISATION_REGION, handleRemoveOrganisationRegion);

  yield takeEvery(StudentActionTypes.CREATE_EDUCATION_SPONSOR, handleCreateEducationSponsor);
  yield takeEvery(StudentActionTypes.UPDATE_EDUCATION_SPONSOR, handleUpdateEducationSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_EDUCATION_SPONSOR, handleRemoveEducationSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_EDUCATION_REGION, handleRemoveEducationRegion);

  yield takeEvery(StudentActionTypes.CREATE_SCHOLARSHIP_SPONSOR, handleCreateScholarshipSponsor);
  yield takeEvery(StudentActionTypes.UPDATE_SCHOLARSHIP_SPONSOR, handleUpdateScholarshipSponsor);
  yield takeEvery(StudentActionTypes.BULK_UPDATE_SCHOLARSHIP_SPONSOR, handleBulkUpdateScholarshipSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_SCHOLARSHIP_SPONSOR, handleRemoveScholarshipSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_SCHOLARSHIP_REGION, handleRemoveScholarshipRegion);

  yield takeEvery(StudentActionTypes.CREATE_FAMILY_SPONSOR, handleCreateFamilySponsor);
  yield takeEvery(StudentActionTypes.UPDATE_FAMILY_SPONSOR, handleUpdateFamilySponsor);
  yield takeEvery(StudentActionTypes.REMOVE_FAMILY_SPONSOR, handleRemoveFamilySponsor);
  yield takeEvery(StudentActionTypes.REMOVE_FAMILY_REGION, handleRemoveFamilyRegion);

  yield takeEvery(StudentActionTypes.CREATE_OTHER_SPONSOR, handleCreateOtherSponsor);
  yield takeEvery(StudentActionTypes.UPDATE_OTHER_SPONSOR, handleUpdateOtherSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_OTHER_SPONSOR, handleRemoveOtherSponsor);
  yield takeEvery(StudentActionTypes.REMOVE_OTHER_REGION, handleRemoveOtherRegion);
}

function* watchMarkMessagesRead(): Generator {
  yield takeEvery(StudentActionTypes.MARK_MESSAGES_READ, handleMarkMessagesRead);
}

function* watchMarkMessageUnread(): Generator {
  yield takeEvery(StudentActionTypes.MARK_MESSAGE_UNREAD, handleMarkMessageUnread);
}

function* watchUpdateConflict(): Generator {
  yield takeEvery(StudentActionTypes.UPDATE_CONFLICT, handleUpdateConflict);
}

function* watchApproveAgentSection(): Generator {
  yield takeEvery(StudentActionTypes.APPROVE_AGENT_SECTION, handleApproveAgentSection);
}

function* watchSaveAgentDetails(): Generator {
  yield takeEvery(StudentActionTypes.SAVE_AGENT_DETAILS, handleSaveAgentDetails);
}

function* watchStudentDelete(): Generator {
  yield takeEvery(StudentActionTypes.DELETE_STUDENT, handleDeleteStudent);
}

function* watchStudentMarkActive(): Generator {
  yield takeEvery(StudentActionTypes.MARK_ACTIVE, handleMarkStudentActive);
}

function* watchStudentRemoveImportHash(): Generator {
  yield takeEvery(StudentActionTypes.REMOVE_IMPORT_HASH, handleRemoveImportHash);
}

function* watchStudentRevokeAccess(): Generator {
  yield takeEvery(StudentActionTypes.REVOKE_STUDENT_ACCESS, handleRevokeStudentAccess);
}

function* watchStudentChangeStudentNo(): Generator {
  yield takeEvery(StudentActionTypes.CHANGE_STUDENT_NO, handleChangeStudentNo);
}

function* watchDeletedFile(): Generator {
  yield takeEvery(StudentActionTypes.DELETE_DOCUMENT_FILE, handleDeleteFile);
  yield takeEvery(StudentActionTypes.DELETE_FINANCE_FILE, handleDeleteFinanceFile);
  yield takeEvery(StudentActionTypes.DELETE_FOLLOW_UP_FILE, handleDeleteFollowUpFile);
  yield takeEvery(StudentActionTypes.DELETE_INTERVIEW_FILE, handleDeleteInterviewFile);
  yield takeEvery(StudentActionTypes.DELETE_RECORDED_INTERVIEW_FILE, handleDeleteRecordedInterviewFile);
}

function* watchStudentLoaded(): Generator {
  // Listen for both fresh data fetch and returning to a cached student page
  yield takeEvery(
    [StudentActionTypes.GET_STUDENT_RECORD_SUCCESS, StudentActionTypes.REGISTER_STUDENT_VIEW],
    handleRegisterStudent,
  );
}

function* watchStudentClosed(): Generator {
  yield takeEvery(StudentActionTypes.CLOSE_STUDENT, handleStudentClosed);
}

function* watchRecordedInterviewAssessment(): Generator {
  yield takeEvery(RecordedInterviewActionTypes.RECORDED_INTERVIEW_ASSESSMENT, handleRecordedInterviewAssessment);
}

function* watchRecordedInterviewScoreEdit(): Generator {
  yield takeEvery(
    RecordedInterviewActionTypes.RECORDED_INTERVIEW_EDIT_QUESTION_SCORE,
    handleRecordedInterviewEditQuestionScore,
  );
}

function* watchVideoProcessingStatus(): Generator {
  yield takeEvery(RecordedInterviewActionTypes.REQUEST_VIDEO_PROCESSING_STATUS, handlVideoProcessingStatus);
}

function* handleSaveDocumentWithFiles(action: ReturnType<typeof docActions.saveDocument>): Generator {
  const context = yield getContext('sagaContext');
  const idToken: any = yield call(context.getIdToken);
  if (!idToken) {
    yield put(logout());
    yield call(context.onLogout);
    console.log('Refresh Token has Expired - handleSaveDocumentWithFiles');
    return;
  }

  const { uni, applicantId, offerId, id, docType, fileNames, s3Keys, docKey } = action.payload;

  let saveDocumentResponse;

  try {
    saveDocumentResponse = yield call(
      api.saveDocumentWithFiles,
      idToken,
      uni,
      applicantId,
      docType,
      fileNames,
      s3Keys,
      docKey,
    );
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to save document files';
    yield put(docActions.saveDocumentFailure(errorMessage));
    return;
  }
  console.log('saveDocumentResponse', saveDocumentResponse);
  try {
    let response;
    if (id === 'proof-of-payment') {
      response = yield call(api.updateOfferProofOfPayment, idToken, uni, applicantId, offerId, {
        fileName: fileNames[0],
        ...saveDocumentResponse,
      });
    } else {
      response = yield call(
        api.updateRequirementDocument,
        idToken,
        uni,
        applicantId,
        offerId,
        id,
        saveDocumentResponse,
      );
    }

    yield put(
      docActions.saveDocumentSuccess({
        documents: response.documents,
        docsComplete: response.docsComplete,
        docsRequired: response.docsRequired,
      }),
    );
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to update requirement document';
    yield put(docActions.saveDocumentFailure(`Document saved but failed to update requirements: ${errorMessage}`));
  }
}

/**
 * Utility function to check if a document s3Key exists in the original application documents
 * If the s3Key is found in application document fields, it should NOT be deleted
 */
function isDocumentFromApplication(application: any, docType: string, s3Key: string): boolean {
  try {
    // Map docType to application document field names
    const docTypeToFieldMap: Record<string, string> = {
      passport: 'passportDocument',
      englishTest: 'englishTestDocument',
      academicCertificate: 'academicCertificateDocument',
      personalStatement: 'personalStatementDocument',
      cv: 'cvDocument',
      referenceLetter: 'referenceLetterDocument',
      photoId: 'idDocument',
      academicTranscript: 'academicTranscriptDocument',
    };

    const documentFieldName = docTypeToFieldMap[docType];
    if (!documentFieldName) {
      return false;
    }

    // Check if the document field exists in the application
    const applicationDocument = application[documentFieldName];
    if (!applicationDocument || typeof applicationDocument !== 'object') {
      return false;
    }

    // Check if the s3Key exists in the application document's s3Keys
    if (applicationDocument.s3Keys && Array.isArray(applicationDocument.s3Keys)) {
      return applicationDocument.s3Keys.includes(s3Key);
    }

    return false;
  } catch (error) {
    console.warn('Error checking if document is from application:', error);
    return false;
  }
}

function* handleDeleteDocument(action: ReturnType<typeof docActions.deleteDocument>): Generator {
  const context = yield getContext('sagaContext');
  const idToken: any = yield call(context.getIdToken);
  if (!idToken) {
    yield put(logout());
    yield call(context.onLogout);
    console.log('Refresh Token has Expired - handleDeleteDocument');
    return;
  }

  const { uni, applicantId, docType, docKey, s3Key, sKey, offerId, requirementId } = action.payload;
  const FILE_NOT_FOUND_INDEX = -1;

  try {
    // Check if this document is from the original application (should NOT be deleted)
    const rootState: ApplicationState = yield select();
    const application = rootState.apply.application || {};

    // Check if the document exists in application document fields
    const isApplicationDocument = isDocumentFromApplication(application, docType, s3Key);

    // Get the current document to retrieve existing files
    const currentDocResponse = yield call(api.getApplicantDocuments, idToken, uni, applicantId, docType);

    if ('error' in currentDocResponse) {
      yield put(docActions.deleteDocumentFailure(`Failed to get document: ${currentDocResponse.error}`));
      return;
    }

    // Find the specific document by docKey
    const documents = currentDocResponse.docs || {};
    const currentDoc = documents[docKey];

    if (!currentDoc) {
      yield put(docActions.deleteDocumentFailure('Document not found'));
      return;
    }

    // Remove the specific file from arrays
    const currentFileNames = currentDoc.fileNames || [];
    const currentS3Keys = currentDoc.s3Keys || [];

    // Find the index of the file to remove by s3Key
    const fileIndex = currentS3Keys.findIndex((key: string) => key === s3Key);

    if (fileIndex === FILE_NOT_FOUND_INDEX) {
      yield put(docActions.deleteDocumentFailure('File not found in document'));
      return;
    }

    // Create new arrays without the removed file
    const updatedFileNames = currentFileNames.filter((_: string, index: number) => index !== fileIndex);
    const updatedS3Keys = currentS3Keys.filter((_: string, index: number) => index !== fileIndex);
    // If no files remain, delete the entire document
    if (updatedFileNames.length === 0) {
      if (!isApplicationDocument) {
        const deleteResponse = yield call(api.deleteDocumentFile, idToken, uni, applicantId, docType, docKey);

        if ('error' in deleteResponse) {
          yield put(docActions.deleteDocumentFailure(`Failed to delete document: ${deleteResponse.error}`));
          return;
        }
      }

      const response = yield call(api.updateRequirementDocument, idToken, uni, applicantId, offerId, requirementId, {
        s3Keys: [],
        sKey: '',
        isDeleted: true,
      });

      if ('error' in response) {
        yield put(docActions.deleteDocumentFailure(`Failed to update requirement: ${response.error}`));
        return;
      }

      try {
        const rootState: ApplicationState = yield select();
        // Extract all tasks from hierarchical requirements and find the matching task
        const allTasks = getAllTasksFromRequirements(rootState.apply.offer.requirements);
        const currentTask = allTasks.find(task => task.id === requirementId);

        if (currentTask && currentTask.fulfilmentData) {
          const currentFulfilmentData = currentTask.fulfilmentData as any;
          const updatedFulfilmentData = {
            ...currentFulfilmentData,
            s3Keys: [],
            urls: [],
          };

          const updatedTask = {
            ...currentTask,
            fulfilmentData: updatedFulfilmentData,
          };

          yield put(updateApplicantRequirementStatusSuccess(updatedTask));
        }
      } catch (error) {
        console.warn('Failed to update requirement in apply store:', error);
      }

      yield put(docActions.deleteDocumentSuccess({ s3key: s3Key }));
    } else {
      const updatedDocument = {
        ...currentDoc,
        fileNames: updatedFileNames,
        s3Keys: updatedS3Keys,
        sKey,
      };
      // If the document is not from the original application, update the document
      let updateResponse = {
        s3Keys: updatedS3Keys,
        sKey,
      };

      if (!isApplicationDocument) {
        updateResponse = yield call(
          api.updateDocumentFiles,
          idToken,
          uni,
          applicantId,
          docType,
          docKey,
          updatedDocument,
        );

        if ('error' in updateResponse) {
          yield put(docActions.deleteDocumentFailure(`Failed to update document: ${updateResponse.error}`));
          return;
        }
      }

      const response = yield call(
        api.updateRequirementDocument,
        idToken,
        uni,
        applicantId,
        offerId,
        requirementId,
        updateResponse,
      );

      if ('error' in response) {
        yield put(docActions.deleteDocumentFailure(`Failed to update requirement: ${response.error}`));
        return;
      }

      try {
        const rootState: ApplicationState = yield select();
        // Extract all tasks from hierarchical requirements and find the matching task
        const allTasks = getAllTasksFromRequirements(rootState.apply.offer.requirements);
        const currentTask = allTasks.find(task => task.id === requirementId);

        if (currentTask && currentTask.fulfilmentData) {
          const currentFulfilmentData = currentTask.fulfilmentData as any;
          const updatedFulfilmentData = {
            ...currentFulfilmentData,
            s3Keys: currentFulfilmentData.s3Keys?.filter((key: string) => key !== s3Key) || [],
            urls:
              currentFulfilmentData.urls?.filter((_: any, index: number) => {
                const correspondingS3Key = currentFulfilmentData.s3Keys?.[index];
                return correspondingS3Key !== s3Key;
              }) || [],
          };

          const updatedTask = {
            ...currentTask,
            fulfilmentData: updatedFulfilmentData,
          };

          yield put(updateApplicantRequirementStatusSuccess(updatedTask));
        }
      } catch (error) {
        console.warn('Failed to update requirement in apply store:', error);
      }

      yield put(docActions.deleteDocumentSuccess({ s3key: s3Key }));
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to delete document file';
    yield put(docActions.deleteDocumentFailure(`Document failed to delete: ${errorMessage}`));
  }
}

function* StudentSaga(): Generator {
  yield all([fork(watchSavePersonalInfo)]);
  yield all([fork(watchSaveHistory)]);
  yield all([fork(watchSaveConsents)]);
  yield all([fork(watchSaveFinance)]);
  yield all([fork(watchSaveVisaPrepForm)]);
  yield all([fork(watchSaveInterviewDocuments)]);
  yield all([fork(watchRequestInterview)]);
  yield all([fork(watchRequestRecordedInterview)]);
  yield all([fork(watchCancelInterview)]);
  yield all([fork(watchCancelRecordedInterview)]);
  yield all([fork(watchFailInterview)]);
  yield all([fork(watchResitInterview)]);
  yield all([fork(watchPassInterview)]);
  yield all([fork(watchSaveRecordedInterviewDocuments)]);
  yield all([fork(watchFailRecordedInterview)]);
  yield all([fork(watchResitRecordedInterview)]);
  yield all([fork(watchPassRecordedInterview)]);
  yield all([fork(watchSavePCI)]);
  yield all([fork(watchSaveDocuments)]);
  yield all([fork(watchSaveDocumentWithFiles)]);
  yield all([fork(watchSaveInternalDocumentWithFiles)]);
  yield all([fork(watchDeleteDocumentFromRequirement)]);
  yield all([fork(watchSaveFinanceDocument)]);
  yield all([fork(watchSaveFollowUpDocument)]);
  yield all([fork(watchSaveFollowUpDocumentsShareCode)]);
  yield all([fork(watchSaveRisks)]);
  yield all([fork(watchWSMessage)]);
  yield all([fork(watchGetStudentRecord)]);
  yield all([fork(watchGetStudentDocs)]);
  yield all([fork(watchGetAuditlog)]);
  yield all([fork(watchGetStudentFinDocs)]);
  yield all([fork(watchGetStudentMessages)]);
  yield all([fork(watchGetStudentFollowUpDocs)]);
  yield all([fork(watchGetStudentRecordedInterviews)]);
  yield all([fork(watchGetStudentCalendlyEvents)]);
  yield all([fork(watchUpdateVisaRefusalMetadata)]);
  yield all([fork(watchUpdateTBTestDocMeta)]);
  yield all([fork(watchNoBiometrics)]);
  yield all([fork(watchApproveDocument)]);
  yield all([fork(watchRejectDocument)]);
  yield all([fork(watchRescindRejectDocument)]);
  yield all([fork(watchDeleteDocument)]);
  yield all([fork(watchAcknowledgeDocument)]);
  yield all([fork(watchApproveFinDocument)]);
  yield all([fork(watchRejectFinDocument)]);
  yield all([fork(watchRescindRejectFinDocument)]);
  yield all([fork(watchDeleteFinDocument)]);
  yield all([fork(watchAcknowledgeFinDocument)]);
  yield all([fork(watchApproveFollowUpDocument)]);
  yield all([fork(watchRejectFollowUpDocument)]);
  yield all([fork(watchRescindRejectFollowUpDocument)]);
  yield all([fork(watchDeleteFollowUpDocument)]);
  yield all([fork(watchUpdateAssignedAgents)]);
  yield all([fork(watchUpdateUniStudentNumber)]);
  yield all([fork(watchUpdateOfferEvidence)]);
  yield all([fork(watchUpdateStatus)]);
  yield all([fork(watchUpdateVisaStatus)]);
  yield all([fork(watchUpdateArrivalStatus)]);
  yield all([fork(watchOverrideMaturityDate)]);
  yield all([fork(watchUpdateMessage)]);
  yield all([fork(watchDownloadAttachments)]);
  yield all([fork(watchMarkMessagesRead)]);
  yield all([fork(watchMarkMessageUnread)]);
  yield all([fork(watchUpdateConflict)]);
  yield all([fork(watchApproveAgentSection)]);
  yield all([fork(watchSaveAgentDetails)]);
  yield all([fork(watchRequestCustomDocument)]);
  yield all([fork(watchRequestFinanceCustomDocument)]);
  yield all([fork(watchRequestFollowUpCustomDocument)]);
  yield all([fork(watchStudentDelete)]);
  yield all([fork(watchStudentMarkActive)]);
  yield all([fork(watchStudentRemoveImportHash)]);
  yield all([fork(watchStudentRevokeAccess)]);
  yield all([fork(watchStudentChangeStudentNo)]);
  yield all([fork(watchDeletedFile)]);
  yield all([fork(watchStudentLoaded)]);
  yield all([fork(watchStudentClosed)]);
  yield all([fork(watchRecordedInterviewAssessment)]);
  yield all([fork(watchRecordedInterviewScoreEdit)]);
  yield all([fork(watchVideoProcessingStatus)]);

  yield takeEvery(actions.requestScoresRegeneration, handleScoresRegeneration);
  yield takeEvery(
    [StudentActionTypes.TOGGLE_SCHEDULED_INTERVIEW_SUCCESS, StudentActionTypes.UPDATE_CALENDLY_EVENT_WS],
    handleOfferCalendlyInterviewInfoStatusUpdate,
  );
}

export default StudentSaga;
